/**
 * ResourceRequestPlanModal
 *
 * Full-screen centred modal for the Suggested Project Plan.
 * Left panel: request summary (fixed). Right panel: scenarios (scrollable).
 * Each proposed booking slot is editable — resource + dates can be adjusted
 * before accepting. Edits are local to the modal session.
 * Evo Design System — semantic tokens only.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  format,
  parseISO,
  isValid,
  addYears,
  differenceInCalendarDays,
  endOfMonth,
  endOfYear,
  min as minDate,
  max as maxDate,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { Calendar, CalendarDayButton } from '@/components/ui/calendar';
import { getNavMonths, type NavMonths } from '@/lib/datePickerNav';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  User,
  Users,
  Zap,
  CheckCircle2,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
  Building2,
  Tag,
  Loader2,
  Check,
  Star,
  Clock,
  Pencil,
  RotateCcw,
  Info,
  AlertCircle,
  Eye,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { roundHours, sumHours } from '@/lib/hours';
import { usePlanResourceDays, type PlanResourceDays } from '@/hooks/usePlanResourceDays';
import {
  ResourceRequest,
  PlanScenario,
  PlanPreviewScenario,
  ProposedBookingSlot,
  DailyAllocation,
  ScenarioStrategy,
  AcceptedRequestBooking,
} from '@/types/resourceRequest';
import { AcceptPlanConfirmDialog } from '@/components/unallocated/AcceptPlanConfirmDialog';
import { RequestedSkillBadges, skillsSectionLabel } from '@/components/unallocated/RequestedSkillBadges';
import { useOverlayScrollable } from '@/hooks/useOverlayScrollable';
import { MatchScoreBadge } from '@/components/booking/MatchScoreBadge';
import { Absence, Resource, Booking } from '@/types';
import { computeAvailabilitySignal, computeRecommendations } from '@/lib/recommendationEngine';
import {
  bookedHoursByDay,
  computePlanDateBounds,
  dayKey,
  dayKeysInRange,
  effectiveWorkingProfile,
  profileHoursForDate,
  isBlockingValidation,
  monthlyHoursForSlot,
  sumMonthlyHours,
  unavailableDayKeys,
  validatePlanSlots,
  validateSlotEdit,
  type MonthlyBudget,
  type PlanDateBounds,
  type SlotValidationError,
  type UnavailableDay,
} from '@/lib/slotEditValidation';
import { isDayWithinContract } from '@/lib/contractWindow';
import { clampSuggestedSlotsToCapacity } from '@/lib/planSlotCapacity';
import {
  resourceUnavailableDays,
  unavailableDaysByResourceId as unavailableDaysForResources,
} from '@/lib/resourceUnavailableDays';
import {
  dayIntervalsFromWindows,
  distributeAcrossFreeWindows,
  relocateDayWindow,
  DEFAULT_BUSINESS_HOURS_END,
  DEFAULT_BUSINESS_HOURS_START,
  type DayInterval,
} from '@/lib/dayAvailability';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { absencesApi, bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { mockAbsences, mockBankHolidays } from '@/lib/mock-data';
import {
  buildPlanPreviewScenario,
  countProposedBookings,
  computeRequestMonthlyHints,
  distributeFromServerSlots,
} from '@/lib/resourceRequestPlanner';
import { MatchSignal, UtilisationContext } from '@/types/recommendation';
import type { DailyAvailabilitySlot, DailyUtilisationSlot } from '@/services/api';
import {
  useProjectBudget,
  findRoleBudget,
  parseMonthlyHours,
  budgetHealthAccent,
} from '@/hooks/useProjectBudget';
import type { ProjectMonthWindow } from '@/hooks/useProjectBudget';
import { liveBreakdownMonths, liveMonthlySpread, liveStartMonth, summaryStartDate } from '@/lib/demandMonthWindow';
import type { EBProjectsBudgetComposite } from '@/services/api';
import { isOperationallyActiveResource } from '@/lib/resource-filters';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  try { return format(parseISO(iso), 'd MMM yyyy'); } catch { return iso; }
}

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// ── Utilisation capacity context ───────────────────────────────────────
// Working profiles, bank holidays, absences and booking types, so the criteria
// breakdown scores utilisation with the same rules as the planner and heatmap.

const UtilisationContextValue = React.createContext<UtilisationContext | undefined>(undefined);

function useUtilisationContext(): UtilisationContext | undefined {
  return React.useContext(UtilisationContextValue);
}

/**
 * Whether the drawer reads a slice of the plan window itself: only when it is
 * open, nobody has handed it that slice, and no read of it is in flight.
 */
export function shouldReadPlanWindow(
  open: boolean,
  passed: unknown,
  pending: boolean,
): boolean {
  return open && passed === undefined && !pending;
}

/** Absences overlapping the request window, so absent days reduce capacity. */
function useRequestAbsences(request: ResourceRequest | null, enabled: boolean): Absence[] {
  const { currentCompany } = useCompany();
  const { isDemo } = useDataMode();
  const demoMode = isDemo('calendar');
  const [absences, setAbsences] = useState<Absence[]>([]);

  const startDate = request?.requestedStartDate;
  const endDate = request?.requestedEndDate ?? request?.requestedStartDate;
  const companyId = currentCompany?.id;

  useEffect(() => {
    if (!enabled || !startDate || !endDate) return;
    if (demoMode) {
      setAbsences(mockAbsences);
      return;
    }
    if (!companyId) return;

    let cancelled = false;
    absencesApi.getByDateRange(new Date(startDate).toISOString(), new Date(endDate).toISOString(), companyId)
      .then(data => { if (!cancelled) setAbsences(data); })
      .catch(() => { if (!cancelled) setAbsences([]); });
    return () => { cancelled = true; };
  }, [enabled, demoMode, companyId, startDate, endDate]);

  return absences;
}

/**
 * The bookings inside the plan window, so the drawer knows *when* a resource is
 * booked and not only how many hours each of its days carries.
 *
 * In live mode the client never bulk-fetches allocated bookings — the drawer is
 * handed an empty list and the backend's per-day booked hours — so the window's
 * bookings are fetched here. In demo mode the caller already passes them in.
 */
function usePlanWindowBookings(bounds: PlanDateBounds | null, enabled: boolean): Booking[] {
  const { currentCompany } = useCompany();
  const { isDemo } = useDataMode();
  const demoMode = isDemo('calendar');
  const [bookings, setBookings] = useState<Booking[]>([]);

  const minDate = bounds?.minDate;
  const maxDate = bounds?.maxDate;
  const companyId = currentCompany?.id;

  useEffect(() => {
    if (!enabled || demoMode || !minDate || !maxDate || !companyId) {
      setBookings([]);
      return;
    }
    let cancelled = false;
    bookingsApi
      .getByDateRange(
        new Date(`${minDate}T00:00:00Z`).toISOString(),
        new Date(`${maxDate}T23:59:59Z`).toISOString(),
        false,
        companyId,
      )
      .then(apiBookings => {
        if (!cancelled) setBookings(apiBookings.map(mapApiBookingToBooking));
      })
      .catch(() => { if (!cancelled) setBookings([]); });
    return () => { cancelled = true; };
  }, [enabled, demoMode, companyId, minDate, maxDate]);

  return bookings;
}

// ── Flagged days in the edit calendars ────────────────────────────────────

const UNAVAILABLE_DAY_MESSAGE = 'Resource is fully booked on this day';

/** The plan window, so slot dates can't be moved outside the request. */
const PlanDateBoundsContext = React.createContext<PlanDateBounds | null>(null);

/** The company's business hours, which bound the day windows a slot can book. */
interface BusinessHours {
  start: number;
  end: number;
}

const BusinessHoursContext = React.createContext<BusinessHours>({
  start: DEFAULT_BUSINESS_HOURS_START,
  end: DEFAULT_BUSINESS_HOURS_END,
});

function useBusinessHours(): BusinessHours {
  return React.useContext(BusinessHoursContext);
}

function usePlanDateBounds(): PlanDateBounds | null {
  return React.useContext(PlanDateBoundsContext);
}

/**
 * The role's monthly budget alongside what each of the plan's slots allocates,
 * so an edited slot can be judged against the months the rest of the plan
 * leaves budget for. Null when the project has no budget for the role.
 */
interface PlanBudget {
  budgetedHoursByMonth: Record<string, number>;
  /** Hours allocated per month by everything but the given slot. */
  allocatedHoursByMonthExcluding: (slotId: string) => Record<string, number>;
}

const PlanBudgetContext = React.createContext<PlanBudget | null>(null);

/** How a non-blocking validation result reads on the slot it belongs to. */
function budgetWarningText(result: SlotValidationError): string {
  return `Over budget: ${result.message} — you can still accept this plan.`;
}

/** The budget an edited slot is judged against, when one is known. */
function useSlotMonthlyBudget(slotId: string): MonthlyBudget | null {
  const planBudget = React.useContext(PlanBudgetContext);
  return useMemo(() => {
    if (!planBudget) return null;
    return {
      budgetedHoursByMonth: planBudget.budgetedHoursByMonth,
      allocatedHoursByMonth: planBudget.allocatedHoursByMonthExcluding(slotId),
    };
  }, [planBudget, slotId]);
}

/**
 * A day the resource is already booked on: selectable like any other, but
 * flagged, and it explains itself on hover. Picking one is reported as a
 * validation error, which spells the reason out and blocks Accept — clearer
 * than a day that cannot be clicked and does not say why.
 */
function BookedDayButton({ reason, className, ...props }: React.ComponentProps<typeof CalendarDayButton> & { reason: string }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <CalendarDayButton
            {...props}
            aria-description={reason}
            className={cn(
              'text-destructive underline decoration-dotted decoration-destructive underline-offset-2',
              className,
            )}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Years either side of today the month/year dropdowns can always reach. */
const NAV_YEARS_BACK  = 2;
const NAV_YEARS_AHEAD = 5;

/**
 * The months the year/month dropdowns can navigate between.
 *
 * react-day-picker stops a `captionLayout="dropdown"` calendar at the end of the
 * current year unless it is given an `endMonth`, which puts a request starting
 * in a later year out of reach. The window therefore always spans a few years
 * around today and is stretched to cover the plan's own dates.
 */
function useNavMonths(): NavMonths {
  const bounds = usePlanDateBounds();
  return useMemo(() => {
    const anchors = bounds ? [parseISO(bounds.minDate), parseISO(bounds.maxDate)] : [];
    return getNavMonths(anchors, {
      yearsBack: NAV_YEARS_BACK,
      yearsAhead: NAV_YEARS_AHEAD,
    });
  }, [bounds]);
}

/**
 * react-day-picker props that flag the resource's booked days and open up the
 * month/year navigation.
 *
 * Nothing in the calendar is disabled — neither a booked day nor one outside the
 * plan window. Picking either is reported as a validation error instead, so the
 * reason is spelled out rather than left as an unexplained gap in the calendar.
 */
interface CalendarDayProps {
  startMonth: Date;
  endMonth: Date;
  components?: React.ComponentProps<typeof Calendar>['components'];
}

function useCalendarDayProps(unavailableDays?: Set<string>): CalendarDayProps {
  const navMonths = useNavMonths();
  return useMemo(() => {
    if (!unavailableDays || unavailableDays.size === 0) return navMonths;

    const isBooked = (date: Date) => unavailableDays.has(dayKey(date));

    return {
      ...navMonths,
      components: {
        DayButton: (props: React.ComponentProps<typeof CalendarDayButton>) =>
          isBooked(props.day.date)
            ? <BookedDayButton {...props} reason={UNAVAILABLE_DAY_MESSAGE} />
            : <CalendarDayButton {...props} />,
      },
    };
  }, [unavailableDays, navMonths]);
}

// ── Slot edit state ───────────────────────────────────────────────────────────

interface SlotEdit {
  resourceId?:       string;
  resourceName?:     string;
  resourcePhotoUrl?: string;
  startDate?:        string;
  endDate?:          string;
  hours?:            number;
  /**
   * The days the slot books, rebuilt for a resource the planner picked by hand.
   * Present only once the slot has been reassigned; per-day overrides are keyed
   * on this breakdown from then on.
   */
  dailyBreakdown?:   DailyAllocation[];
  /** Per-day overrides: key = day index in dailyBreakdown */
  dailyEdits?: Record<number, { date?: string; startTime: string; endTime: string }>;
}

type SlotEdits = Map<string, SlotEdit>;

/** Apply per-day edits to a breakdown, recomputing hours from the time window. */
function applyDailyEdits(
  breakdown: DailyAllocation[],
  dailyEdits?: Record<number, { date?: string; startTime: string; endTime: string }>,
): DailyAllocation[] {
  if (!dailyEdits) return breakdown;
  return breakdown.map((d, i) => {
    const de = dailyEdits[i];
    if (!de) return d;
    const effectiveDate = de.date ?? d.date;
    const mins = timeToMinutes(de.endTime) - timeToMinutes(de.startTime);
    // An end at or before the start books nothing; validation surfaces it and
    // holds the edit rather than silently keeping the suggested hours.
    const hours = mins > 0 ? Math.round((mins / 60) * 10) / 10 : 0;
    return {
      ...d,
      date:      effectiveDate,
      startTime: `${effectiveDate}T${de.startTime}:00`,
      endTime:   `${effectiveDate}T${de.endTime}:00`,
      hours,
    };
  });
}

/**
 * The days a slot actually books, which is what its daily breakdown lists — not
 * the span of its date range, most of which it books nothing on (a slot skips
 * weekends, non-working days and the days its resource is already busy). Only a
 * slot with no breakdown books its range end to end.
 */
function bookingDayCount(slot: ProposedBookingSlot): number {
  const breakdown = slot.dailyBreakdown;
  if (breakdown && breakdown.length > 0) return breakdown.length;
  try {
    const days = differenceInCalendarDays(parseISO(slot.endDate), parseISO(slot.startDate)) + 1;
    return days > 0 ? days : 1;
  } catch {
    return 1;
  }
}

function bookingDaysLabel(slot: ProposedBookingSlot): string {
  const days = bookingDayCount(slot);
  return days === 1 ? '1 day' : `${days} days`;
}

function mergeSlot(slot: ProposedBookingSlot, edit?: SlotEdit): ProposedBookingSlot {
  if (!edit) return slot;
  const { dailyEdits, ...rest } = edit;
  const merged = { ...slot, ...rest };
  if (dailyEdits && merged.dailyBreakdown) {
    merged.dailyBreakdown = applyDailyEdits(merged.dailyBreakdown, dailyEdits);
    // Recalculate range from the actual daily dates after edits
    const dates = merged.dailyBreakdown.map(d => d.date).filter(Boolean).sort();
    if (dates.length > 0) {
      merged.startDate = dates[0];
      merged.endDate   = dates[dates.length - 1];
    }
  }
  return merged;
}

// ── Resource slot grouping ────────────────────────────────────────────────────

interface ResourceSlotGroup {
  resourceId:       string;
  resourceName:     string;
  resourceRole:     string;
  resourcePhotoUrl?: string;
  softSignalsMet:   number;
  monthKey:         string; // representative month key for the first slot
  slots:            ProposedBookingSlot[];
  totalHours:       number;
  firstStart:       string;
  lastEnd:          string;
}

/**
 * Group proposed slots by their effective (post-edit) resource id, preserving
 * slot order. A reassigned slot's role is the picked resource's, not the one the
 * planner proposed.
 */
export function groupSlotsByResource(
  slots: ProposedBookingSlot[],
  edits: SlotEdits,
  resources: Resource[],
): ResourceSlotGroup[] {
  const roleById = new Map(resources.map(r => [String(r.id), r.role]));
  const map = new Map<string, ResourceSlotGroup>();
  for (const slot of slots) {
    // Use the effective (post-edit) resourceId so reassigned slots move groups
    const edit    = edits.get(slot.id);
    const groupId = edit?.resourceId ?? slot.resourceId;
    if (!map.has(groupId)) {
      map.set(groupId, {
        resourceId:       edit?.resourceId      ?? slot.resourceId,
        resourceName:     edit?.resourceName    ?? slot.resourceName,
        resourceRole:     edit?.resourceId ? roleById.get(String(edit.resourceId)) ?? '' : slot.resourceRole,
        resourcePhotoUrl: edit?.resourcePhotoUrl ?? slot.resourcePhotoUrl,
        softSignalsMet:   slot.softSignalsMet,
        monthKey:         slot.monthKey,
        slots:            [],
        totalHours:       0,
        firstStart:       slot.startDate,
        lastEnd:          slot.endDate,
      });
    }
    const group = map.get(groupId)!;
    group.slots.push(slot);
    group.totalHours = roundHours(group.totalHours + (edit?.hours ?? slot.hours));
    if (slot.startDate < group.firstStart) group.firstStart = slot.startDate;
    if (slot.endDate   > group.lastEnd)   group.lastEnd     = slot.endDate;
  }
  return Array.from(map.values());
}

// ── Strategy config ───────────────────────────────────────────────────────────

interface StrategyMeta {
  icon:        React.ReactNode;
  accent:      string;
  ringAccent:  string;
  recommended?: boolean;
}

const STRATEGY_META: Record<ScenarioStrategy, StrategyMeta> = {
  'single-resource': {
    icon:      <User className="h-4 w-4" />,
    accent:    'accent-purple',
    ringAccent:'ring-[var(--accent-purple-500)]',
  },
  'spread-load': {
    icon:      <Users className="h-4 w-4" />,
    accent:    'accent-teal',
    ringAccent:'ring-[var(--accent-teal-500)]',
    recommended: true,
  },
  'fastest-start': {
    icon:      <Zap className="h-4 w-4" />,
    accent:    'accent-warning',
    ringAccent:'ring-[var(--accent-warning-500)]',
  },
};

// ── Editable Slot Row ─────────────────────────────────────────────────────────

interface SlotRowProps {
  slot:            ProposedBookingSlot;
  edit?:           SlotEdit;
  resources:       Resource[];
  existingBookings: Booking[];
  requestSkills:   string[];
  requestRole?:    string;
  utilisationByResourceId?: Record<string, number>;
  utilisationDaysByResourceId?: Record<string, DailyUtilisationSlot[]>;
  /** Free time windows per resource id, as the planner measured them. */
  availabilityByResourceId?: Record<string, DailyAvailabilitySlot[]>;
  isEditing:       boolean;
  onEdit:          (id: string, patch: SlotEdit) => void;
  onReset:         (id: string) => void;
  /** Reports the slot's current validation message (null = valid) */
  onValidityChange?: (slotId: string, message: string | null) => void;
}

// ── Daily breakdown summary (visible inline) ─────────────────────────────────
function formatTime(isoStr: string): string {
  try {
    return formatInTimeZone(parseISO(isoStr), 'UTC', 'HH:mm');
  } catch { return ''; }
}

/**
 * Scores a single assigned resource against the request so the badge can show
 * the same criteria breakdown as the resource picker.
 */
/**
 * The calendar month a slot belongs to. Utilisation is reported for the whole month
 * (as the planner does) rather than the slot's own dates: a slot only covers the days
 * it could allocate, which excludes exactly the days already taken by bookings.
 */
function monthPeriod(monthKey?: string): { start: Date; end: Date } | undefined {
  const parsed = monthKey ? /^(\d{4})-(\d{2})$/.exec(monthKey) : null;
  if (!parsed) return undefined;
  const year = Number(parsed[1]);
  const month = Number(parsed[2]);
  return { start: new Date(year, month - 1, 1), end: new Date(year, month, 0) };
}

/**
 * The days a month's slot may be re-fitted into when its resource changes: the
 * whole month it allocates to, clamped to the plan window.
 *
 * The slot's own range is no use here — it only covers the days the previous
 * resource had free, which for a nearly-full month can be a single day. Refitting
 * inside it leaves the hours on days the new resource may be fully booked on, so
 * the search widens to the month, the unit the plan spreads hours over.
 */
function replanRange(
  monthKey: string | undefined,
  fallback: { startDate: string; endDate: string },
  bounds: PlanDateBounds | null,
): { startDate: string; endDate: string } {
  const period = monthPeriod(monthKey);
  if (!period) return fallback;

  const startDate = maxDayKey(dayKey(period.start), bounds?.minDate);
  const endDate   = minDayKey(dayKey(period.end),   bounds?.maxDate);
  return endDate < startDate ? fallback : { startDate, endDate };
}

function maxDayKey(day: string, limit?: string): string {
  return limit && limit > day ? limit : day;
}

function minDayKey(day: string, limit?: string): string {
  return limit && limit < day ? limit : day;
}

/**
 * Criteria a match score is out of: skills is only scored when the request asks
 * for skills, so a skills-less request is judged on utilisation + role alone.
 */
function criteriaTotal(requestSkills: string[] | undefined): number {
  return (requestSkills?.length ?? 0) > 0 ? 3 : 2;
}

function useSlotSignals({ resourceId, startDate, endDate, monthKey, resources, existingBookings, requestSkills, requestRole, utilisationByResourceId, utilisationDaysByResourceId }: {
  resourceId:       string;
  startDate:        string;
  endDate:          string;
  /** 'YYYY-MM' the slot belongs to; utilisation is reported for this whole month. */
  monthKey?:        string;
  resources:        Resource[];
  existingBookings: Booking[];
  requestSkills:    string[];
  requestRole?:     string;
  utilisationByResourceId?: Record<string, number>;
  utilisationDaysByResourceId?: Record<string, DailyUtilisationSlot[]>;
}): MatchSignal[] {
  const utilisationContext = useUtilisationContext();
  return useMemo(() => {
    const resource = (resources ?? []).find(r => String(r.id) === String(resourceId));
    if (!resource) return [];
    try {
      const start = parseISO(startDate);
      const end   = parseISO(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
      const result = computeRecommendations({
        requiredRole:   requestRole ?? '',
        requiredSkills: requestSkills ?? [],
        startDate:      start,
        endDate:        end,
        allResources:   [resource],
        existingBookings,
        utilisationByResourceId,
        utilisationDaysByResourceId,
        utilisationContext,
        utilisationPeriod: monthPeriod(monthKey),
      });
      return [...result.cleanMatches, ...result.conflictMatches][0]?.signals ?? [];
    } catch {
      return [];
    }
  }, [resourceId, startDate, endDate, monthKey, resources, existingBookings, requestSkills, requestRole, utilisationByResourceId, utilisationDaysByResourceId, utilisationContext]);
}

// ── Time-window helpers ───────────────────────────────────────────────────────

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

/** Minutes since midnight for an "HH:mm" string. */
function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** World-class two-column time picker popover */
function TimeSelect({ value, onChange, minTime, className }: {
  value: string;
  onChange: (v: string) => void;
  /** Times at or before this "HH:mm" cannot be picked — used by the end time. */
  minTime?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [hh, mm] = value.split(':');
  const hourColumn = useOverlayScrollable<HTMLDivElement>();
  const minuteColumn = useOverlayScrollable<HTMLDivElement>();
  const minMinutes = minTime !== undefined ? timeToMinutes(minTime) : null;

  /** The earliest minute option that keeps `h` after `minTime`, if any. */
  const firstValidMinute = (h: string): string | undefined =>
    MINUTES.find(m => minMinutes === null || timeToMinutes(`${h}:${m}`) > minMinutes);

  const isHourDisabled = (h: string) => firstValidMinute(h) === undefined;
  const isMinuteDisabled = (m: string) =>
    minMinutes !== null && timeToMinutes(`${hh}:${m}`) <= minMinutes;

  // Scroll selected item into centre when opened
  React.useEffect(() => {
    if (!open) return;
    const scrollTo = (ref: React.RefObject<HTMLDivElement | null>, val: string, items: string[]) => {
      if (!ref.current) return;
      const idx = items.indexOf(val);
      if (idx === -1) return;
      const child = ref.current.children[idx] as HTMLElement;
      if (child) ref.current.scrollTop = child.offsetTop - ref.current.clientHeight / 2 + child.clientHeight / 2;
    };
    requestAnimationFrame(() => {
      scrollTo(hourColumn.nodeRef, hh, HOURS);
      scrollTo(minuteColumn.nodeRef, mm, MINUTES);
    });
  }, [open, hh, mm, hourColumn.nodeRef, minuteColumn.nodeRef]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'h-9 px-3 rounded-md border border-input bg-background',
            'text-sm font-medium tabular-nums text-foreground',
            'hover:bg-accent hover:border-primary/50 transition-colors cursor-pointer',
            'flex items-center gap-2 min-w-[90px] justify-between',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            open && 'border-primary/50 ring-2 ring-primary/20',
            className,
          )}
          onClick={e => { e.stopPropagation(); setOpen(true); }}
        >
          <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="flex-1 text-left">{value}</span>
          <ChevronDown className={cn('w-3 h-3 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-44 shadow-xl" align="start" sideOffset={4} onClick={e => e.stopPropagation()}>
        {/* Column headers */}
        <div className="grid grid-cols-2 border-b border-border/60 bg-muted/30">
          <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3 border-r border-border/60">Hour</div>
          <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2 px-3">Min</div>
        </div>
        <div className="flex">
          {/* Hours column */}
          <div
            ref={hourColumn.ref}
            className="flex-1 h-56 overflow-y-auto border-r border-border/40"
          >
            {HOURS.map(h => {
              const disabled = isHourDisabled(h);
              return (
                <button
                  key={h}
                  type="button"
                  disabled={disabled}
                  className={cn(
                    'w-full text-left text-sm py-2 px-3 transition-colors',
                    h === hh
                      ? 'bg-primary text-primary-foreground font-semibold'
                      : 'hover:bg-accent text-foreground/80 hover:text-foreground',
                    disabled && 'opacity-30 cursor-not-allowed hover:bg-transparent',
                  )}
                  onClick={() => {
                    // Keep the picked hour valid: snap to the earliest minute that
                    // still lands after `minTime`.
                    const keepsMinute = minMinutes === null || timeToMinutes(`${h}:${mm}`) > minMinutes;
                    const minute = keepsMinute ? mm : firstValidMinute(h);
                    if (minute) onChange(`${h}:${minute}`);
                  }}
                >
                  {h}
                </button>
              );
            })}
          </div>
          {/* Minutes column */}
          <div
            ref={minuteColumn.ref}
            className="flex-1 h-56 overflow-y-auto"
          >
            {MINUTES.map(m => {
              const disabled = isMinuteDisabled(m);
              return (
                <button
                  key={m}
                  type="button"
                  disabled={disabled}
                  className={cn(
                    'w-full text-left text-sm py-2 px-3 transition-colors',
                    m === mm
                      ? 'bg-primary text-primary-foreground font-semibold'
                      : 'hover:bg-accent text-foreground/80 hover:text-foreground',
                    disabled && 'opacity-30 cursor-not-allowed hover:bg-transparent',
                  )}
                  onClick={() => { onChange(`${hh}:${m}`); setOpen(false); }}
                >
                  :{m}
                </button>
              );
            })}
          </div>
        </div>
        {/* Footer */}
        <div className="border-t border-border/60 px-3 py-2 flex items-center justify-between bg-muted/20">
          <span className="text-xs text-muted-foreground">Selected</span>
          <span className="text-sm font-bold tabular-nums text-foreground">{value}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Daily breakdown summary ───────────────────────────────────────────────────

/** Extracts "HH:mm" from a full ISO datetime string or "HH:mm" string. */
function extractHHMM(timeStr: string): string {
  if (!timeStr) return '09:00';
  if (timeStr.includes('T')) {
    const parts = timeStr.split('T');
    return parts[1]?.substring(0, 5) ?? '09:00';
  }
  return timeStr.substring(0, 5);
}

function DailyBreakdownSummary({
  slot,
  breakdown: breakdownProp,
  dailyEdits,
  onDayEdit,
  unavailableDays,
}: {
  slot: ProposedBookingSlot;
  breakdown?: DailyAllocation[];
  /** Per-day edits: key = day index */
  dailyEdits?: Record<number, { date?: string; startTime: string; endTime: string }>;
  /** Called when user changes a day's date or times; undefined = read-only mode */
  onDayEdit?: (index: number, date: string, startTime: string, endTime: string) => void;
  /** 'yyyy-MM-dd' days the assigned resource is already booked out on */
  unavailableDays?: Set<string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const breakdown = breakdownProp ?? slot.dailyBreakdown;
  if (!breakdown || breakdown.length === 0) return null;

  const isEditable = !!onDayEdit;
  const PREVIEW_COUNT = isEditable ? breakdown.length : 3; // always show all in edit mode
  const showToggle = !isEditable && breakdown.length > PREVIEW_COUNT;
  const visible = expanded ? breakdown : breakdown.slice(0, PREVIEW_COUNT);

  const totalHours = sumHours(breakdown.map(d => d.hours ?? 0));
  const dayCount   = breakdown.length;

  return (
    <div>
      {/* Summary header — only in read-only mode; edit mode shows it in the compact row */}
      {!isEditable && (
        <div className="flex items-center justify-between px-4 py-1.5 border-b border-border/30 bg-muted/20">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {dayCount} {dayCount === 1 ? 'day' : 'days'} planned
          </span>
          <span className="text-xs font-bold text-foreground tabular-nums">
            {totalHours}h total
          </span>
        </div>
      )}

      {/* Rows */}
      <div className={cn('divide-y divide-border/20', isEditable && 'px-4 pb-1.5')}>
        {visible.map((d, i) => {
          let dateLabel = d.date;
          try { dateLabel = format(parseISO(d.date), 'EEE d MMM'); } catch { /* keep raw */ }

          if (isEditable) {
            const currentDate  = dailyEdits?.[i]?.date      ?? d.date;
            const currentStart = dailyEdits?.[i]?.startTime ?? extractHHMM(d.startTime ?? '');
            const currentEnd   = dailyEdits?.[i]?.endTime   ?? extractHHMM(d.endTime   ?? '');
            const diffMins  = timeToMinutes(currentEnd) - timeToMinutes(currentStart);
            // A booking sits inside one day, so an end at or before the start is
            // invalid rather than an overnight window.
            const isInvalidWindow = diffMins <= 0;
            const liveHours = Math.round((Math.max(diffMins, 0) / 60) * 10) / 10;
            const isDayEdited = !!dailyEdits?.[i];

            return (
              <div key={i} className={cn(
                'flex items-center gap-3 py-1.5 transition-colors',
                isDayEdited && 'bg-primary/[3%]',
              )} onClick={e => e.stopPropagation()}>
                {/* Date picker */}
                <SingleDatePicker
                  value={currentDate}
                  onChange={newDate => onDayEdit!(i, newDate, currentStart, currentEnd)}
                  isEdited={isDayEdited}
                  unavailableDays={unavailableDays}
                />
                {/* Times */}
                <div className="flex items-center gap-2 ml-auto shrink-0">
                  <TimeSelect value={currentStart} onChange={v => onDayEdit!(i, currentDate, v, currentEnd)} />
                  <span className="text-muted-foreground text-sm">→</span>
                  <TimeSelect
                    value={currentEnd}
                    minTime={currentStart}
                    onChange={v => onDayEdit!(i, currentDate, currentStart, v)}
                    className={isInvalidWindow ? 'border-destructive text-destructive' : undefined}
                  />
                </div>
                {/* Hours */}
                <span className={cn(
                  'text-sm font-bold tabular-nums shrink-0 w-10 text-right',
                  isInvalidWindow ? 'text-destructive' : isDayEdited ? 'text-primary' : 'text-foreground/70',
                )}>
                  {liveHours}h
                </span>
              </div>
            );
          }

          // Read-only row
          const timeRange = d.startTime && d.endTime
            ? `${formatTime(d.startTime)}–${formatTime(d.endTime)}`
            : null;
          return (
            <React.Fragment key={i}>
              <div className="flex items-center justify-between px-4 py-1.5 text-xs">
                <span className="text-muted-foreground">{dateLabel}</span>
                <div className="flex items-center gap-4">
                  {timeRange && <span className="text-muted-foreground tabular-nums">{timeRange}</span>}
                  <span className="font-semibold tabular-nums text-foreground w-8 text-right">{d.hours}h</span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {showToggle && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}
          className="flex items-center gap-1 pt-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {expanded
            ? <><ChevronUp className="h-3 w-3" /> Show less</>
            : <><ChevronDown className="h-3 w-3" /> +{breakdown.length - PREVIEW_COUNT} more</>
          }
        </button>
      )}
    </div>
  );
}

// ── Single date picker (popover calendar) ────────────────────────────────────
interface SingleDatePickerProps {
  value: string;           // 'yyyy-MM-dd'
  onChange: (v: string) => void;
  isEdited?: boolean;
  /** 'yyyy-MM-dd' days the assigned resource is already booked out on */
  unavailableDays?: Set<string>;
}

function SingleDatePicker({ value, onChange, isEdited, unavailableDays }: SingleDatePickerProps) {
  const [open, setOpen] = useState(false);
  const calendarDayProps = useCalendarDayProps(unavailableDays);
  const parsed = useMemo(() => { try { const d = parseISO(value); return isValid(d) ? d : undefined; } catch { return undefined; } }, [value]);

  const label = parsed ? format(parsed, 'EEE d MMM') : 'Select date';

  const handleDayClick = (day: Date) => {
    onChange(format(day, 'yyyy-MM-dd'));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={e => e.stopPropagation()}
          className={cn(
            'h-9 px-3 rounded-md border border-input bg-background',
            'text-sm font-medium text-foreground',
            'hover:bg-accent hover:border-primary/50 transition-colors cursor-pointer',
            'flex items-center gap-2 min-w-[130px]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isEdited && 'border-primary/40 text-primary',
            open && 'border-primary/50 ring-2 ring-primary/20',
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="flex-1 text-left">{label}</span>
          <ChevronDown className={cn('w-3 h-3 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-auto"
        align="start"
        side="bottom"
        sideOffset={4}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <div className="flex flex-col">
          <div className="px-4 pt-3 pb-2 border-b border-border/60">
            <span className="text-xs font-semibold text-foreground">Select date</span>
          </div>
          <Calendar
            mode="single"
            selected={parsed}
            onDayClick={handleDayClick}
            captionLayout="dropdown"
            defaultMonth={parsed ?? new Date()}
            className="p-3"
            {...calendarDayProps}
          />
          {parsed && (
            <div className="border-t border-border/60 px-4 py-2 flex items-center gap-1.5 text-[10px] bg-background">
              <span className="text-muted-foreground font-medium">Selected:</span>
              <span className="font-semibold text-foreground">{format(parsed, 'EEE d MMM yyyy')}</span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Editable slot row (hooks for dropdown + recommendation engine) ─────────────
// ── Resource availability, shared by the resource picker and the slot editor ──

/**
 * The availability lookups a manual reassignment needs: a resource's own
 * contracted hours, the parts of a day an absence takes off them, and the days a
 * number of hours becomes when laid onto their free time.
 */
function useResourceAvailability({
  resources, existingBookings, availabilityByResourceId, utilisationDaysByResourceId,
}: {
  resources:        Resource[];
  existingBookings: Booking[];
  /** Free time windows per resource id, as the planner measured them. */
  availabilityByResourceId?: Record<string, DailyAvailabilitySlot[]>;
  /**
   * The backend's per-day booked hours per resource id — in live mode the only
   * account of the bookings a resource already has.
   */
  utilisationDaysByResourceId?: Record<string, DailyUtilisationSlot[]>;
}) {
  const utilisationContext = useUtilisationContext();
  const businessHours = useBusinessHours();

  const resourceById = useMemo(
    () => new Map((resources ?? []).map(r => [String(r.id), r])),
    [resources],
  );

  const globalWorkingProfile = utilisationContext?.globalWorkingProfile;

  /** The selected resource's own contracted hours per weekday. */
  const profileFor = useCallback(
    (resourceId: string) =>
      effectiveWorkingProfile(resourceById.get(resourceId), globalWorkingProfile),
    [resourceById, globalWorkingProfile],
  );

  /**
   * The bank holidays and absences taking hours off the resource's days in a
   * range — the unavailability no booking accounts for.
   */
  const holidayAbsenceDaysFor = useCallback(
    (resourceId: string, startDate: Date, endDate: Date): UnavailableDay[] =>
      resourceUnavailableDays({
        resourceId,
        startDate,
        endDate,
        bankHolidays: utilisationContext?.bankHolidays,
        absences: utilisationContext?.absences,
        workingProfile: profileFor(resourceId),
        businessHoursStart: businessHours.start,
        businessHoursEnd: businessHours.end,
      }),
    [utilisationContext?.bankHolidays, utilisationContext?.absences, profileFor, businessHours],
  );

  /** The parts of a day an absence takes, which nothing can be booked into. */
  const absentIntervalsFor = useCallback(
    (resourceId: string, date: string): DayInterval[] => {
      const day = parseISO(date);
      if (!isValid(day)) return [];
      return dayIntervalsFromWindows(
        holidayAbsenceDaysFor(resourceId, day, day).flatMap(d => d.lostWindows ?? []),
      );
    },
    [holidayAbsenceDaysFor],
  );

  /**
   * The days `hours` become on `resourceId`, laid onto that resource's own free
   * windows inside `[startDate, endDate]`.
   *
   * The planner's measured availability is used when it has any for the resource,
   * so a reassignment is fitted exactly as the initial plan was. Resources the
   * plan never measured (anyone outside its shortlist) are fitted client-side
   * from the same inputs a per-day edit uses.
   */
  const breakdownForResource = useCallback((
    resourceId: string, hours: number, startDate: string, endDate: string,
    daysByResourceId: Record<string, DailyUtilisationSlot[]> | undefined = utilisationDaysByResourceId,
  ): DailyAllocation[] => {
    const start = parseISO(startDate);
    const end   = parseISO(endDate);
    if (!(hours > 0) || !isValid(start) || !isValid(end) || end < start) return [];

    // Bank holidays and whole days of absence are not bookable at all, and the
    // free-window walk below cannot see them: they take no named window off a day.
    const unavailable = holidayAbsenceDaysFor(resourceId, start, end);
    const wholeDaysLost = new Set(
      unavailable
        .filter(day => day.lostHours == null)
        .map(day => day.date.slice(0, 10)),
    );
    const partialLossByDay = new Map(
      unavailable
        .filter(day => day.lostHours != null)
        .map(day => [day.date.slice(0, 10), day.lostHours ?? 0]),
    );

    const profile = profileFor(resourceId);
    const reportedDays = daysByResourceId?.[resourceId];
    // Days the resource is already booked solid on. In live mode these are only
    // known from the backend's per-day figures, which the free-window walk cannot
    // see — without this, hours land on a day validation then reports as booked.
    const bookedSolid = unavailableDayKeys(
      existingBookings, resourceId, profile, reportedDays,
    );
    const bookedHours = bookedHoursByDay(existingBookings, resourceId, reportedDays);

    const resource = resourceById.get(resourceId);
    const dates = dayKeysInRange(start, end)
      .filter(date => !wholeDaysLost.has(date) && !bookedSolid.has(date))
      .filter(date => isDayWithinContract(resource, date));
    if (dates.length === 0) return [];

    const measured = (availabilityByResourceId?.[resourceId] ?? [])
      .filter(free => dates.includes(free.date.slice(0, 10)));
    if (measured.length > 0) {
      const distribution = distributeFromServerSlots(
        measured, hours, businessHours.start, businessHours.end,
      );
      return distribution?.dailyAllocations ?? [];
    }

    // A free window's times are wall-clock, and the plan pipeline reads its
    // timestamps as UTC, so they are stamped as UTC rather than being shifted by
    // the viewer's offset.
    return distributeAcrossFreeWindows({
      existingBookings,
      resourceId,
      dates,
      hours,
      workingProfile: profile,
      businessHoursStart: businessHours.start,
      businessHoursEnd: businessHours.end,
      blockedIntervalsFor: date => absentIntervalsFor(resourceId, date),
      // What a day has left once the hours already booked on it, and any hours an
      // absence takes off it, are set against its contracted hours.
      hoursCapFor: (date) => {
        const day = parseISO(date);
        if (!isValid(day)) return 0;
        return roundHours(
          profileHoursForDate(day, profile)
          - (bookedHours.get(date) ?? 0)
          - (partialLossByDay.get(date) ?? 0),
        );
      },
    }).map(allocation => ({
      date:      allocation.date,
      hours:     allocation.hours,
      startTime: `${allocation.date}T${allocation.startTime}:00Z`,
      endTime:   `${allocation.date}T${allocation.endTime}:00Z`,
    }));
  }, [
    availabilityByResourceId, utilisationDaysByResourceId, businessHours, existingBookings,
    profileFor, absentIntervalsFor, holidayAbsenceDaysFor, resourceById,
  ]);

  return {
    businessHours, resourceById, profileFor,
    holidayAbsenceDaysFor, absentIntervalsFor, breakdownForResource,
  };
}

interface RankedResource {
  resource:           Resource;
  softSignalsMet:     number;
  availabilityStatus: 'free' | 'partial' | 'conflict';
  signals:            MatchSignal[];
}

/** Every resource scored against the request over a range, best match first. */
function useRankedResources({
  resources, existingBookings, requestSkills, requestRole,
  startDate, endDate, monthKey, utilisationByResourceId, utilisationDaysByResourceId,
}: {
  resources:        Resource[];
  existingBookings: Booking[];
  requestSkills:    string[];
  requestRole?:     string;
  startDate:        string;
  endDate:          string;
  monthKey?:        string;
  utilisationByResourceId?:     Record<string, number>;
  utilisationDaysByResourceId?: Record<string, DailyUtilisationSlot[]>;
}): RankedResource[] {
  const utilisationContext = useUtilisationContext();
  return useMemo(() => {
    try {
      const start = parseISO(startDate);
      const end   = parseISO(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) throw new Error('invalid dates');
      const result = computeRecommendations({
        requiredRole:    requestRole ?? '',
        requiredSkills:  requestSkills ?? [],
        startDate:       start,
        endDate:         end,
        allResources:    resources,
        existingBookings,
        utilisationByResourceId,
        utilisationDaysByResourceId,
        utilisationContext,
        utilisationPeriod: monthPeriod(monthKey),
      });
      const all = [...result.cleanMatches, ...result.conflictMatches];
      return all.map(m => ({
        resource: m.resource,
        softSignalsMet: m.softSignalsMet,
        availabilityStatus: m.availabilityStatus,
        signals:  m.signals,
      }));
    } catch {
      return (resources ?? []).map(r => ({ resource: r, softSignalsMet: 0, availabilityStatus: 'free' as const, signals: [] as MatchSignal[] }));
    }
  }, [
    resources, existingBookings, requestSkills, requestRole, startDate, endDate,
    monthKey, utilisationByResourceId, utilisationDaysByResourceId, utilisationContext,
  ]);
}

/**
 * Whether a resource meets what the request actually asks for: its role and, when
 * the request names skills, those skills. Spare capacity is left out — it ranks a
 * match rather than qualifying it, so a qualified resource is still offered when
 * the month is busy.
 */
function meetsRequestCriteria(entry: RankedResource): boolean {
  const qualifying = qualifyingSignals(entry);
  return qualifying.length > 0 && qualifying.every(signal => signal.met);
}

/**
 * Whether a resource meets any criterion at all, spare capacity included — the
 * least a stand-in can do. A resource scoring "0 of 3 met" has nothing to
 * recommend it, so it is never offered.
 */
function meetsAnyRequestCriteria(entry: RankedResource): boolean {
  return entry.signals.some(signal => signal.met);
}

/**
 * The signals that qualify a resource for the request. Spare capacity is left
 * out: it ranks a match rather than qualifying it, so a qualified resource is
 * still offered when the month is busy.
 */
function qualifyingSignals(entry: RankedResource): MatchSignal[] {
  return entry.signals.filter(signal => signal.key !== 'utilisation');
}

/** An availability pill: how much of the range the resource has free. */
function AvailabilityPill({ status, className }: {
  status: RankedResource['availabilityStatus'];
  className?: string;
}) {
  return (
    <span className={cn(
      'text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap',
      status === 'free'
        ? 'bg-[var(--accent-teal-100)] text-[var(--accent-teal-700)]'
        : status === 'partial'
          ? 'bg-[var(--accent-warning-100)] text-[var(--accent-warning-700)]'
          : 'bg-destructive/10 text-destructive',
      className,
    )}>
      {status === 'free' ? 'Free' : status === 'partial' ? 'Partial' : 'Busy'}
    </span>
  );
}

/**
 * The field the whole allocation's resource is changed in. Only resources that
 * meet the request's criteria are offered: the planner is choosing a stand-in for
 * a suggestion, not browsing the directory. When none meets them all, those
 * meeting at least one are offered — but a resource meeting none of them never
 * is, so the list stays a shortlist rather than the directory.
 */
export function ResourcePickerField({
  resourceId, resourceName, resourcePhotoUrl, ranked, requestSkills, onSelect, loading = false,
}: {
  resourceId:        string;
  resourceName:      string;
  resourcePhotoUrl?: string;
  ranked:            RankedResource[];
  requestSkills:     string[];
  onSelect:          (resourceId: string, resourceName: string, resourcePhotoUrl?: string) => void;
  /** While the picked resource's availability is still being read. */
  loading?:          boolean;
}) {
  const [open, setOpen] = useState(false);

  const options = useMemo(() => {
    const activeRanked = ranked.filter(({ resource }) => isOperationallyActiveResource(resource));
    const qualified = activeRanked.filter(meetsRequestCriteria);
    const offered   = qualified.length > 0 ? qualified : activeRanked.filter(meetsAnyRequestCriteria);
    // The assigned resource is always listed, even when it no longer qualifies:
    // the field has to be able to show what the slot is on.
    return offered.some(entry => String(entry.resource.id) === String(resourceId))
      ? offered
      : [...ranked.filter(entry => String(entry.resource.id) === String(resourceId)), ...offered];
  }, [ranked, resourceId]);

  const current = ranked.find(entry => String(entry.resource.id) === String(resourceId));

  return (
    <div className="flex items-center gap-2 min-w-0" onClick={e => e.stopPropagation()}>
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={resourcePhotoUrl} />
        <AvatarFallback className="text-[10px]">
          {initials(resourceName)}
        </AvatarFallback>
      </Avatar>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-busy={loading || undefined}
            className={cn(
              'flex-1 min-w-0 h-8 flex items-center justify-between gap-2 rounded-lg border px-3 text-xs transition-all',
              'bg-background hover:bg-accent/40 border-input hover:border-primary/40',
              open && 'ring-2 ring-primary/30 border-primary/40',
            )}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="font-semibold truncate">{resourceName}</span>
              {current?.resource.role && (
                <span className="text-[10px] text-muted-foreground truncate hidden sm:block">
                  · {current.resource.role}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {current && <AvailabilityPill status={current.availabilityStatus} className="rounded-md px-1.5" />}
              {loading
                ? <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" aria-label="Loading availability" />
                : <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform duration-150', open && 'rotate-180')} />}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[460px]"
          align="start"
          side="bottom"
          sideOffset={4}
          onClick={e => e.stopPropagation()}
        >
          {/* Scroll container wraps BOTH header and rows so scrollbar width affects both equally */}
          <div className="max-h-72 overflow-y-scroll divide-y divide-border/30">
            {/* Column headers — sticky at top, same grid as every data row. The
                background is opaque: a translucent one lets the rows scrolling
                under the header read through it. */}
            <div className="grid px-3 py-2 border-b bg-muted sticky top-0 z-10 items-center gap-x-3"
                 style={{ gridTemplateColumns: '40px 1fr 96px 88px' }}>
              <div />
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Resource</div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Avail.</div>
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider text-center">Match</div>
            </div>
            {options.map(({ resource: r, softSignalsMet: score, availabilityStatus, signals }) => {
              const rid       = String(r.id);
              const isCurrent = rid === String(resourceId);
              return (
                <div
                  key={rid}
                  className={cn(
                    'grid px-3 py-2.5 items-center gap-x-3 transition-colors hover:bg-accent/40 cursor-pointer',
                    isCurrent && 'bg-primary/5',
                  )}
                  style={{ gridTemplateColumns: '40px 1fr 96px 88px' }}
                  onClick={() => {
                    onSelect(rid, r.name, r.photoUrl);
                    setOpen(false);
                  }}
                >
                  {/* Col 1 — Avatar 28px */}
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={r.photoUrl} />
                    <AvatarFallback className="text-[9px]">
                      {initials(r.name)}
                    </AvatarFallback>
                  </Avatar>

                  {/* Col 2 — Name + role */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold truncate">{r.name}</span>
                      {isCurrent && (
                        <span className="flex items-center justify-center h-4 w-4 rounded-full bg-primary shrink-0">
                          <Check className="h-2.5 w-2.5 text-primary-foreground" />
                        </span>
                      )}
                    </div>
                    {r.role && (
                      <span className="text-[10px] text-muted-foreground leading-tight">{r.role}</span>
                    )}
                  </div>

                  {/* Col 3 — Availability pill, left-aligned to match header */}
                  <div className="flex justify-start">
                    <AvailabilityPill status={availabilityStatus} />
                  </div>

                  {/* Col 4 — Match score, centred in 88px column. Its own hover
                      carries the criteria breakdown. */}
                  <div
                    className="flex items-center justify-center"
                    onClick={e => e.stopPropagation()}
                  >
                    <MatchScoreBadge
                      softSignalsMet={score}
                      signals={signals}
                      totalCriteria={criteriaTotal(requestSkills)}
                      tooltipSide="left"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function SlotRowEditing({ slot, edit, resources, existingBookings, requestSkills, requestRole, utilisationByResourceId, utilisationDaysByResourceId, availabilityByResourceId, onEdit, onReset, onValidityChange }: Omit<SlotRowProps, 'isEditing'> & { onValidityChange?: (slotId: string, message: string | null) => void; onDoneEditing?: () => void }) {
  // An invalid edit is held here rather than committed, so `slotEdits` only ever
  // carries edits that pass validation.
  const [draftEdit, setDraftEdit] = useState<SlotEdit | undefined>(undefined);
  const [error, setError] = useState<SlotValidationError | null>(null);
  /** Budget overage on the slot — informational, so it never holds the edit. */
  const [budgetWarning, setBudgetWarning] = useState<string | null>(null);
  /** What a date change had to change about the hours, so the user is told. */
  const [adjustment, setAdjustment] = useState<string | null>(null);

  const effectiveEdit = draftEdit ?? edit;
  const merged  = mergeSlot(slot, effectiveEdit);
  const isDirty = !!effectiveEdit && Object.keys(effectiveEdit).length > 0;
  /**
   * The days the panel edits, before any per-day override: the suggestion's own
   * days, or the days rebuilt for a resource the planner has since picked. Per-day
   * overrides are keyed by index into this, so both must come from one place.
   */
  const baseBreakdown = useMemo(
    () => effectiveEdit?.dailyBreakdown ?? slot.dailyBreakdown ?? [],
    [effectiveEdit?.dailyBreakdown, slot.dailyBreakdown],
  );
  const planDateBounds = usePlanDateBounds();
  const monthlyBudget = useSlotMonthlyBudget(slot.id);
  const { businessHours, resourceById, profileFor, holidayAbsenceDaysFor, absentIntervalsFor } =
    useResourceAvailability({ resources, existingBookings, availabilityByResourceId });

  // Drop any held draft once the slot is reset back to the suggestion
  useEffect(() => {
    if (!edit) {
      setDraftEdit(undefined);
      setError(null);
      setBudgetWarning(null);
      setAdjustment(null);
    }
  }, [edit]);

  // Closing the edit panel discards the draft, so the slot is judged on its
  // committed state again — which the drawer validates on its own.
  useEffect(() => () => onValidityChange?.(slot.id, null), [slot.id, onValidityChange]);

  /**
   * Days the assigned resource cannot take this slot on — any day their working
   * profile gives hours to that already carries a booking, plus the days a bank
   * holiday or a full day's absence takes whole, so the pickers show them as
   * unavailable rather than letting a booking be moved onto Christmas Day.
   *
   * The backend's per-day figures are the only source of existing bookings in
   * live mode, where the client never bulk-fetches the allocated ones.
   */
  const unavailableDays = useMemo(
    () => {
      const resourceId = String(merged.resourceId);
      const days = unavailableDayKeys(
        existingBookings,
        resourceId,
        profileFor(resourceId),
        utilisationDaysByResourceId?.[resourceId],
      );
      const from = planDateBounds ? parseISO(planDateBounds.minDate) : parseISO(merged.startDate);
      const to = planDateBounds ? parseISO(planDateBounds.maxDate) : parseISO(merged.endDate);
      if (!isValid(from) || !isValid(to)) return days;
      for (const day of holidayAbsenceDaysFor(resourceId, from, to)) {
        if (day.lostHours == null) days.add(day.date);
      }
      return days;
    },
    [
      existingBookings, merged.resourceId, merged.startDate, merged.endDate,
      profileFor, utilisationDaysByResourceId, planDateBounds, holidayAbsenceDaysFor,
    ],
  );

  /**
   * Validates a candidate edit against the assigned resource's availability for
   * the candidate's own date range, so a resource change, a date change and an
   * hours change are all judged the same way.
   */
  const validateCandidate = useCallback((candidate: SlotEdit | undefined): SlotValidationError | null => {
    const candidateMerged = mergeSlot(slot, candidate);
    const resourceId = String(candidateMerged.resourceId);
    const resource   = resourceById.get(resourceId);

    let availabilityStatus: 'free' | 'partial' | 'conflict' | undefined;
    const start = parseISO(candidateMerged.startDate);
    const end   = parseISO(candidateMerged.endDate);
    if (resource && isValid(start) && isValid(end)) {
      const availability = computeAvailabilitySignal(resource, start, end, existingBookings);
      availabilityStatus = availability.isFullyBooked
        ? 'conflict'
        : availability.availablePeriods.some(p => p.type === 'partial')
          ? 'partial'
          : 'free';
    }

    const dailyAllocations = (candidateMerged.dailyBreakdown ?? [])
      .map(d => ({ date: d.date, hours: d.hours, startTime: d.startTime, endTime: d.endTime }));

    // Slots without a daily breakdown book their whole range, so the days to
    // check come from the range itself — otherwise an already-booked day would
    // never be checked for those slots.
    const dayDates = dailyAllocations.length > 0
      ? dailyAllocations.map(d => d.date)
      : isValid(start) && isValid(end) && end >= start
        ? dayKeysInRange(start, end)
        : [];

    // Only the days the user has moved or retimed are judged on their time of
    // day: a suggested day's window is the planner's own pick from the
    // resource's free gaps, so it needs no second opinion.
    const timeWindowCheckDays = Object.entries(candidate?.dailyEdits ?? {})
      .map(([index, dayEdit]) =>
        dayEdit.date
        ?? (candidate?.dailyBreakdown ?? slot.dailyBreakdown)?.[Number(index)]?.date)
      .filter((date): date is string => !!date)
      .map(date => date.slice(0, 10));

    return validateSlotEdit({
      existingBookings,
      resourceId,
      resourceName: candidateMerged.resourceName,
      startDate:    candidateMerged.startDate,
      endDate:      candidateMerged.endDate,
      hours:        candidateMerged.hours,
      dayDates,
      dailyAllocations,
      availabilityStatus,
      planDateBounds,
      workingProfile: profileFor(resourceId),
      monthlyBudget,
      reportedBookedDays: utilisationDaysByResourceId?.[resourceId],
      unavailableDays: isValid(start) && isValid(end) && end >= start
        ? holidayAbsenceDaysFor(resourceId, start, end)
        : undefined,
      timeWindowCheckDays,
      businessHoursStart: businessHours.start,
      businessHoursEnd: businessHours.end,
    });
  }, [
    slot, resourceById, existingBookings, planDateBounds, profileFor, monthlyBudget,
    utilisationDaysByResourceId, businessHours, holidayAbsenceDaysFor,
  ]);

  // The panel judges the state it opens with, not only the edits made in it: a
  // suggested slot can already sit on a day the resource is booked on, and the
  // alert has to say so before anything is changed. Held drafts keep their own
  // error, which describes the change the user has just attempted.
  useEffect(() => {
    if (draftEdit) return;
    const result = validateCandidate(edit);
    setError(isBlockingValidation(result) ? result : null);
    setBudgetWarning(result && !isBlockingValidation(result) ? budgetWarningText(result) : null);
  }, [draftEdit, edit, validateCandidate]);

  /**
   * Commits a patch when nothing blocks it; otherwise holds it as an invalid
   * draft. Budget overage does not block: the edit is committed and the overage
   * shown as a warning, so the plan can still be accepted.
   */
  const applyEdit = useCallback((patch: SlotEdit) => {
    const candidate: SlotEdit = { ...(effectiveEdit ?? {}), ...patch };
    const result = validateCandidate(candidate);
    const blocking = isBlockingValidation(result) ? result : null;
    setError(blocking);
    setBudgetWarning(!blocking && result ? budgetWarningText(result) : null);
    onValidityChange?.(slot.id, blocking?.message ?? null);
    if (blocking) {
      setDraftEdit(candidate);
      return;
    }
    setDraftEdit(undefined);
    onEdit(slot.id, candidate);
  }, [effectiveEdit, validateCandidate, onValidityChange, onEdit, slot.id]);

  const handleReset = useCallback(() => {
    setDraftEdit(undefined);
    setError(null);
    setBudgetWarning(null);
    setAdjustment(null);
    onValidityChange?.(slot.id, null);
    onReset(slot.id);
  }, [onReset, onValidityChange, slot.id]);

  // ── Per-day edit handler (date + times) ─────────────────────────────────────
  const handleDayEdit = useCallback((index: number, date: string, startTime: string, endTime: string) => {
    const prev = effectiveEdit?.dailyEdits?.[index];
    // Keep a fallback time when only the date changed
    const start = startTime || prev?.startTime || startTime;
    const resourceId = String(merged.resourceId);

    // The hours the day has to book: what its window asks for, falling back to
    // the suggestion's own target for that day.
    const requested = roundHours(
      Math.max(0, (timeToMinutes(endTime) - timeToMinutes(start)) / 60),
    );
    const requiredHours = requested > 0
      ? requested
      : roundHours(baseBreakdown[index]?.hours ?? 0);

    // A day the resource is already booked on may have the hours free but not at
    // this time of day, so the window moves to a free one large enough for them.
    // When the day has no such window the asked-for window is kept as it is and
    // validation reports it, rather than a silently shortened booking being made.
    const relocated = relocateDayWindow({
      existingBookings,
      resourceId,
      date,
      startTime: start,
      endTime,
      requiredHours,
      workingProfile: profileFor(resourceId),
      businessHoursStart: businessHours.start,
      businessHoursEnd: businessHours.end,
      blockedIntervals: absentIntervalsFor(resourceId, date),
    });

    setAdjustment(relocated.moved
      ? `${merged.resourceName} is busy at ${start}: moved to `
        + `${relocated.startTime}–${relocated.endTime} on ${fmtDate(date)}`
      : null);

    const newDailyEdits = {
      ...(effectiveEdit?.dailyEdits ?? {}),
      [index]: { date, startTime: relocated.startTime, endTime: relocated.endTime },
    };
    // Recompute total hours from updated breakdown
    const updatedBreakdown = applyDailyEdits(baseBreakdown, newDailyEdits);
    const newTotal = sumHours(updatedBreakdown.map(d => d.hours ?? 0));
    applyEdit({ dailyEdits: newDailyEdits, hours: newTotal });
  }, [
    effectiveEdit?.dailyEdits, baseBreakdown, applyEdit, existingBookings,
    profileFor, businessHours, merged.resourceId, merged.resourceName, absentIntervalsFor,
  ]);

  // The resource is chosen for the whole allocation, in the picker above the
  // months, so the panel only reports how the assigned one scores.
  const rankedResources = useRankedResources({
    resources, existingBookings, requestSkills, requestRole,
    startDate: merged.startDate, endDate: merged.endDate, monthKey: slot.monthKey,
    utilisationByResourceId, utilisationDaysByResourceId,
  });

  const currentRankedResource = rankedResources.find(r => String(r.resource.id) === String(merged.resourceId));

  return (
    <div className="transition-all duration-150">
      {/* ── Resource row: who the slot is on, and the reset back to the suggestion ── */}
      <div
        className="grid items-center gap-x-2 px-4 pt-2"
        style={{ gridTemplateColumns: '32px 1fr auto' }}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Row 1 col 1: Avatar ── */}
        <div className="row-start-1 col-start-1 flex items-center pb-1.5">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage src={merged.resourcePhotoUrl} />
            <AvatarFallback className="text-[10px]">
              {initials(merged.resourceName)}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* ── Row 1 col 2: who the slot is on — changed in the picker above ── */}
        <div className="row-start-1 col-start-2 flex items-center gap-2 min-w-0 pb-1.5">
          <span className="text-xs font-semibold truncate">{merged.resourceName}</span>
          {currentRankedResource?.resource.role && (
            <span className="text-[10px] text-muted-foreground truncate hidden sm:block">
              · {currentRankedResource.resource.role}
            </span>
          )}
          {currentRankedResource && (
            <AvailabilityPill status={currentRankedResource.availabilityStatus} className="shrink-0" />
          )}
        </div>

        {/* ── Row 1 col 3: Match score + Reset ── */}
        <div className="row-start-1 col-start-3 flex items-center pb-1.5">
          <div className="flex items-center gap-1.5 justify-center flex-1">
          <MatchScoreBadge
            softSignalsMet={slot.softSignalsMet}
            signals={currentRankedResource?.signals}
            totalCriteria={criteriaTotal(requestSkills)}
            tooltipSide="left"
          />
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); handleReset(); }}
                  className={cn(
                    'p-1.5 rounded-lg transition-colors shrink-0',
                    isDirty
                      ? 'text-primary hover:bg-primary/10 cursor-pointer'
                      : 'text-muted-foreground/25 cursor-default pointer-events-none',
                  )}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              {isDirty && <TooltipContent side="top" className="text-xs">Reset to suggested</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
          </div>{/* end centred badge+reset group */}
        </div>

      </div>{/* end resource row */}

      {/* Daily breakdown with per-day time editing */}
      <div className="pb-2">
        {/* Validation error — kept at the top of the panel, in plain sight */}
        {error && (
          <div className="px-4 pb-2">
            <Alert variant="destructive" size="compact">
              <AlertDescription className="text-xs font-medium">{error.message}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* Budget overage — allowed, so it reads as a warning, not a block */}
        {!error && budgetWarning && (
          <div className="px-4 pb-2">
            <Alert variant="warning" size="compact">
              <AlertDescription className="text-xs font-medium">{budgetWarning}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* Hours a date change had to cut back to fit the day */}
        {!error && adjustment && (
          <div className="px-4 pb-2">
            <Alert size="compact">
              <AlertDescription className="text-xs font-medium">{adjustment}</AlertDescription>
            </Alert>
          </div>
        )}

        <DailyBreakdownSummary
          slot={slot}
          breakdown={baseBreakdown}
          dailyEdits={effectiveEdit?.dailyEdits}
          onDayEdit={baseBreakdown.length > 0 ? handleDayEdit : undefined}
          unavailableDays={unavailableDays}
        />

        {/* Total hours — what the days below add up to, not set on its own */}
        <div className="flex items-center justify-between gap-3 px-4 pt-1.5" onClick={e => e.stopPropagation()}>
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            Total hours
          </span>
          <span className="text-xs font-bold tabular-nums text-foreground">{merged.hours}h</span>
        </div>

        {/* Live updated-plan preview — the plan these edits produce */}
        {!error && isDirty && (
          <div className="px-4 pt-2">
            <div className="rounded-lg border border-primary/20 bg-primary/5 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-1.5 border-b border-primary/20">
                <span className="flex items-center gap-1.5 text-[10px] font-semibold text-primary uppercase tracking-wider">
                  <CalendarDays className="h-3 w-3" />
                  Updated plan
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {fmtDate(merged.startDate)}
                  {merged.startDate !== merged.endDate && ` – ${fmtDate(merged.endDate)}`}
                </span>
              </div>
              <DailyBreakdownSummary slot={merged} />
            </div>
          </div>
        )}

        {/* Reset link — only shown when dirty */}
        {isDirty && (
          <div className="border-t border-border/20 mt-0.5 pt-1 px-0">
            <button
              type="button"
              onClick={handleReset}
              className="text-[10px] text-muted-foreground/60 hover:text-primary transition-colors flex items-center gap-1"
            >
              <RotateCcw className="h-2.5 w-2.5" />
              Reset to suggestion
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Scenario card ─────────────────────────────────────────────────────────────

// ── Preview button (standalone so it doesn't re-render the full card) ─────────

// ── Compact slot row (inside the resource accordion, non-editing state) ─────

interface SlotRowCompactProps {
  slot:         ProposedBookingSlot;
  edit?:        SlotEdit;
  isEditable:   boolean;
  isEditing:    boolean;
  /** Validation message for the slot's in-progress edit, when it is invalid */
  errorMessage?: string;
  /** Selects the owning scenario; only supplied while it is selectable */
  onSelectScenario?: () => void;
  onToggleEdit: () => void;
  onReset:      () => void;
}

function SlotRowCompact({ slot, edit, isEditable, isEditing, errorMessage, onSelectScenario, onToggleEdit, onReset }: SlotRowCompactProps) {
  const merged  = mergeSlot(slot, edit);
  const isDirty = !!edit && Object.keys(edit).length > 0;

  const durationLabel = bookingDaysLabel(merged);
  let dateLabel = '';
  try {
    const start = parseISO(merged.startDate);
    const end   = parseISO(merged.endDate);
    dateLabel = format(start, 'MMM yyyy') === format(end, 'MMM yyyy')
      ? `${format(start, 'd')}–${format(end, 'd MMM yyyy')}`
      : `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  } catch { /* leave blank */ }

  return (
    <div
      role={onSelectScenario ? 'button' : undefined}
      tabIndex={onSelectScenario ? 0 : undefined}
      aria-label={onSelectScenario ? 'Select this scenario' : undefined}
      onClick={onSelectScenario}
      onKeyDown={onSelectScenario
        ? e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectScenario();
            }
          }
        : undefined}
      className={cn(
        'flex items-center gap-2 px-4 py-2 transition-colors outline-none',
        onSelectScenario && 'cursor-pointer focus-visible:ring-2 focus-visible:ring-ring',
        isEditing  ? 'bg-primary/5'    :
        isDirty    ? 'bg-primary/[3%]' : 'hover:bg-muted/20',
      )}
    >
      <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

      {/* Date */}
      <span className="text-sm font-medium flex-1 min-w-0 truncate">{dateLabel}</span>

      {/* Duration */}
      {durationLabel && (
        <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{durationLabel}</span>
      )}

      {/* Hours — live from breakdown when editing */}
      <span className="text-sm font-bold tabular-nums shrink-0 w-10 text-right">{merged.hours}h</span>

      {/* Edited badge + reset — only when dirty and not currently open */}
      {isDirty && !isEditing && (
        <>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
            Edited
          </span>
          <button
            type="button"
            title="Reset to suggestion"
            className="text-muted-foreground hover:text-primary transition-colors shrink-0"
            onClick={e => { e.stopPropagation(); onReset(); }}
          >
            <RotateCcw className="h-3 w-3" />
          </button>
        </>
      )}

      {/* The reason the edit cannot be kept, spelled out on the row itself */}
      {errorMessage && (
        <span
          title={errorMessage}
          className="flex items-center gap-1 min-w-0 text-[11px] font-medium text-destructive"
        >
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span className="truncate">{errorMessage}</span>
        </span>
      )}

      {/* Edit / Done toggle */}
      {isEditable && (
        isEditing ? (
          <Button
            variant="default"
            size="sm"
            className="h-6 px-2.5 gap-1 text-[11px] shrink-0"
            disabled={!!errorMessage}
            onClick={e => { e.stopPropagation(); onToggleEdit(); }}
          >
            <Check className="h-3 w-3" />
            Done
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 gap-1 text-[11px] text-muted-foreground hover:text-foreground shrink-0"
            onClick={e => { e.stopPropagation(); onToggleEdit(); }}
          >
            <Pencil className="h-3 w-3" />
            Edit
          </Button>
        )
      )}
    </div>
  );
}

// ── Resource slot group card (accordion grouping all slots for one resource) ──

interface ResourceSlotGroupCardProps {
  group:                        ResourceSlotGroup;
  slotEdits:                    SlotEdits;
  editingSlotIds:               Set<string>;
  isEditable:                   boolean;
  scenarioTotalHours:           number;
  resources:                    Resource[];
  existingBookings:             Booking[];
  requestSkills:                string[];
  requestRole?:                 string;
  utilisationByResourceId?:     Record<string, number>;
  utilisationDaysByResourceId?: Record<string, DailyUtilisationSlot[]>;
  /** What the picker scores resources on; defaults to `utilisationDaysByResourceId`. */
  scoringUtilisationDaysByResourceId?: Record<string, DailyUtilisationSlot[]>;
  /** Free time windows per resource id, as the planner measured them. */
  availabilityByResourceId?:    Record<string, DailyAvailabilitySlot[]>;
  /** Reads a picked resource's days when the plan has only its totals. */
  resourceDays?:                ScenarioResourceDays;
  /** Validation messages for slots whose in-progress edit is invalid */
  slotErrors:                   Map<string, string>;
  /** Selects the owning scenario; only supplied while it is selectable */
  onSelectScenario?:            () => void;
  onSlotEdit:                   (slotId: string, patch: SlotEdit) => void;
  onSlotReset:                  (slotId: string) => void;
  onSlotValidityChange:         (slotId: string, message: string | null) => void;
  onToggleSlotEdit:             (slotId: string) => void;
}

/** One scenario's on-demand reads of a resource's days (see `usePlanResourceDays`). */
interface ScenarioResourceDays {
  needs: (resourceId: string) => boolean;
  load:  (resourceId: string) => Promise<Record<string, DailyUtilisationSlot[]> | undefined>;
}

export function ResourceSlotGroupCard({
  group, slotEdits, editingSlotIds, isEditable, scenarioTotalHours,
  resources, existingBookings, requestSkills, requestRole,
  utilisationByResourceId, utilisationDaysByResourceId, scoringUtilisationDaysByResourceId,
  availabilityByResourceId, resourceDays, slotErrors,
  onSelectScenario, onSlotEdit, onSlotReset, onSlotValidityChange, onToggleSlotEdit,
}: ResourceSlotGroupCardProps) {
  // Single resource groups auto-expand; multi-group scenarios start collapsed
  const [open, setOpen] = useState(true);

  // Represent signals using the first slot's data
  const firstSlot    = group.slots[0];
  const firstEdit    = slotEdits.get(firstSlot.id);
  const firstMerged  = mergeSlot(firstSlot, firstEdit);
  const groupSignals = useSlotSignals({
    resourceId:                  firstMerged.resourceId,
    startDate:                   firstMerged.startDate,
    endDate:                     firstMerged.endDate,
    monthKey:                    firstSlot.monthKey,
    resources,
    existingBookings,
    requestSkills,
    requestRole,
    utilisationByResourceId,
    utilisationDaysByResourceId,
  });

  // Group slots by their display month label (preserving order)
  const byMonth = useMemo(() => {
    const map = new Map<string, ProposedBookingSlot[]>();
    for (const slot of group.slots) {
      if (!map.has(slot.month)) map.set(slot.month, []);
      map.get(slot.month)!.push(slot);
    }
    return map;
  }, [group.slots]);

  const editedCount = group.slots.filter(
    s => slotEdits.has(s.id) && Object.keys(slotEdits.get(s.id)!).length > 0,
  ).length;

  // A slot is one month's allocation, not one booking: accepting it creates a
  // booking per day of its breakdown, the same count the scenario header gives.
  const bookingCount = useMemo(
    () => group.slots.reduce((sum, s) => sum + bookingDayCount(mergeSlot(s, slotEdits.get(s.id))), 0),
    [group.slots, slotEdits],
  );

  const share = scenarioTotalHours > 0
    ? Math.round((group.totalHours / scenarioTotalHours) * 100)
    : 0;

  // ── Resource change for the whole allocation ────────────────────────────────
  const planDateBounds = usePlanDateBounds();
  const { breakdownForResource } = useResourceAvailability({
    resources, existingBookings, availabilityByResourceId, utilisationDaysByResourceId,
  });
  const rankedResources = useRankedResources({
    resources, existingBookings, requestSkills, requestRole,
    startDate: group.firstStart, endDate: group.lastEnd, monthKey: firstSlot.monthKey,
    utilisationByResourceId,
    utilisationDaysByResourceId: scoringUtilisationDaysByResourceId ?? utilisationDaysByResourceId,
  });

  /**
   * Moves every month of the allocation to another resource, re-fitting each
   * month's hours onto the days that resource actually has free rather than
   * keeping the previous resource's days — which only ever reported a conflict.
   *
   * The days are searched for across the whole month (see `replanRange`), so the
   * slot's dates move with its breakdown. Per-day overrides are dropped with the
   * old breakdown: they were made for days the new resource may not even work. A
   * month the resource has no free time in keeps its days, so validation can
   * report what they cannot take.
   */
  const applyResourceChange = useCallback((
    resourceId: string, resourceName: string, resourcePhotoUrl?: string,
    daysByResourceId: Record<string, DailyUtilisationSlot[]> | undefined = utilisationDaysByResourceId,
  ) => {
    for (const slot of group.slots) {
      const merged = mergeSlot(slot, slotEdits.get(slot.id));
      const patch: SlotEdit = { resourceId, resourceName, resourcePhotoUrl, dailyEdits: {} };
      const range = replanRange(slot.monthKey, merged, planDateBounds);
      const rebuilt = breakdownForResource(
        resourceId, roundHours(merged.hours ?? 0), range.startDate, range.endDate, daysByResourceId,
      );
      onSlotEdit(slot.id, rebuilt.length > 0
        ? {
            ...patch,
            dailyBreakdown: rebuilt,
            startDate: rebuilt[0].date.slice(0, 10),
            endDate:   rebuilt[rebuilt.length - 1].date.slice(0, 10),
            hours: sumHours(rebuilt.map(day => day.hours ?? 0)),
          }
        : patch);
    }
  }, [group.slots, slotEdits, breakdownForResource, planDateBounds, onSlotEdit, utilisationDaysByResourceId]);

  // A resource the plan only has totals for is fitted once its days are read; a
  // later pick supersedes one still being read.
  const latestPick = React.useRef(0);
  const [pendingResourceId, setPendingResourceId] = useState<string | null>(null);
  const handleResourceChange = useCallback((
    resourceId: string, resourceName: string, resourcePhotoUrl?: string,
  ) => {
    const pick = ++latestPick.current;
    if (!resourceDays?.needs(resourceId)) {
      setPendingResourceId(null);
      applyResourceChange(resourceId, resourceName, resourcePhotoUrl);
      return;
    }
    setPendingResourceId(resourceId);
    void resourceDays.load(resourceId).then(days => {
      if (pick !== latestPick.current) return;
      setPendingResourceId(null);
      applyResourceChange(resourceId, resourceName, resourcePhotoUrl, days);
    });
  }, [resourceDays, applyResourceChange]);

  // Effective hours per month (respects edits)
  const monthTotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const slot of group.slots) {
      const h = slotEdits.get(slot.id)?.hours ?? slot.hours;
      map.set(slot.month, roundHours((map.get(slot.month) ?? 0) + h));
    }
    return map;
  }, [group.slots, slotEdits]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn(
        'rounded-xl border overflow-hidden transition-colors',
        open ? 'border-border shadow-sm' : 'border-border/60',
      )}>
        {/* ── Accordion trigger / resource header ── */}
        <CollapsibleTrigger
          className="w-full text-left px-4 py-3.5 hover:bg-muted/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onSelectScenario}
        >
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 shrink-0">
              {group.resourcePhotoUrl && (
                <AvatarImage src={group.resourcePhotoUrl} alt={group.resourceName} />
              )}
              <AvatarFallback className="text-[11px]">
                {initials(group.resourceName)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold truncate">{group.resourceName}</span>
                {group.resourceRole && (
                  <span className="text-xs text-muted-foreground truncate">· {group.resourceRole}</span>
                )}
                {editedCount > 0 && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                    {editedCount} edited
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {bookingCount} booking{bookingCount !== 1 ? 's' : ''}
                {' · '}
                {fmtDate(group.firstStart)} – {fmtDate(group.lastEnd)}
              </div>
            </div>

            <div className="shrink-0 text-right mr-1">
              <div className="text-sm font-bold tabular-nums">{group.totalHours}h</div>
              <div className="text-[10px] text-muted-foreground">{share}% of plan</div>
            </div>

            <MatchScoreBadge
              softSignalsMet={group.softSignalsMet}
              signals={groupSignals}
              totalCriteria={criteriaTotal(requestSkills)}
              tooltipSide="left"
            />

            <ChevronDown className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
              open && 'rotate-180',
            )} />
          </div>

          {/* Progress bar */}
          <Progress value={share} className="h-1 mt-2.5" />

          {/* Month pills — visible when collapsed */}
          {!open && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {Array.from(monthTotals.entries()).map(([month, hours]) => (
                <span
                  key={month}
                  className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 text-[10px]"
                >
                  <span className="text-muted-foreground">
                    {month.split(' ')[0]?.slice(0, 3)} {month.split(' ')[1]}
                  </span>
                  <span className="font-semibold tabular-nums">{hours}h</span>
                </span>
              ))}
            </div>
          )}
        </CollapsibleTrigger>

        {/* ── Expanded slot list ── */}
        <CollapsibleContent>
          <div className="border-t border-border/60">
            {/* ── Resource field: one pick for every month of the allocation ── */}
            {isEditable && (
              <div className="px-4 py-2.5 border-b border-border/60 bg-muted/10">
                <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">
                  Resource
                </span>
                <ResourcePickerField
                  resourceId={group.resourceId}
                  resourceName={group.resourceName}
                  resourcePhotoUrl={group.resourcePhotoUrl}
                  ranked={rankedResources}
                  requestSkills={requestSkills}
                  onSelect={handleResourceChange}
                  loading={pendingResourceId !== null}
                />
              </div>
            )}

            {Array.from(byMonth.entries()).map(([month, slots]) => (
              <div key={month}>
                {/* Month sub-header */}
                <div className="flex items-center justify-between px-4 py-1 bg-muted/20">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {month}
                  </span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {(monthTotals.get(month) ?? 0)}h
                  </span>
                </div>

                {/* Slots for this month */}
                <div className="divide-y divide-border/40">
                  {slots.map(slot => {
                    const isEditingThis = editingSlotIds.has(slot.id);
                    return (
                      <div key={slot.id}>
                        {/* Compact summary row — always visible */}
                        <SlotRowCompact
                          slot={slot}
                          edit={slotEdits.get(slot.id)}
                          isEditable={isEditable}
                          isEditing={isEditingThis}
                          errorMessage={slotErrors.get(slot.id)}
                          onSelectScenario={onSelectScenario}
                          onToggleEdit={() => onToggleSlotEdit(slot.id)}
                          onReset={() => onSlotReset(slot.id)}
                        />
                        {/* Inline edit panel — expands below the compact row */}
                        {isEditingThis && (
                          <div className="border-t border-primary/20">
                            <SlotRowEditing
                              slot={slot}
                              edit={slotEdits.get(slot.id)}
                              resources={resources}
                              existingBookings={existingBookings}
                              requestSkills={requestSkills}
                              requestRole={requestRole}
                              utilisationByResourceId={utilisationByResourceId}
                              utilisationDaysByResourceId={utilisationDaysByResourceId}
                              availabilityByResourceId={availabilityByResourceId}
                              onEdit={onSlotEdit}
                              onReset={onSlotReset}
                              onValidityChange={onSlotValidityChange}
                              onDoneEditing={() => onToggleSlotEdit(slot.id)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ── Preview button ─────────────────────────────────────────────────────────────

interface PreviewButtonProps {
  scenario: PlanScenario;
  onPreview: (scenario: PlanScenario) => void;
}

function PreviewButton({ scenario, onPreview }: PreviewButtonProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] px-2 gap-1 shrink-0"
            onClick={(e) => { e.stopPropagation(); onPreview(scenario); }}
          >
            <Eye className="h-3 w-3" />
            Preview on calendar
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[200px]">
          See these proposed bookings overlaid on your current calendar view before accepting
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ScenarioCardProps {
  scenario:              PlanScenario;
  isSelected:            boolean;
  isAccepted?:           boolean;
  resources:             Resource[];
  existingBookings:      Booking[];
  requestSkills:         string[];
  requestRole?:          string;
  slotEdits:             SlotEdits;
  /** Validation messages for slots whose in-progress edit is invalid */
  slotErrors:            Map<string, string>;
  onSelect:              () => void;
  onSlotEdit:            (slotId: string, patch: SlotEdit) => void;
  onSlotReset:           (slotId: string) => void;
  onSlotValidityChange:  (slotId: string, message: string | null) => void;
  onPreviewOnCalendar?:  (scenario: PlanScenario) => void;
  /** The plan's per-day utilisation, including days read since it was generated. */
  resourceDays?:         PlanResourceDays;
}

function ScenarioCard({
  scenario, isSelected, isAccepted,
  resources, existingBookings, requestSkills, requestRole, slotEdits, slotErrors,
  onSelect, onSlotEdit, onSlotReset, onSlotValidityChange, onPreviewOnCalendar, resourceDays,
}: ScenarioCardProps) {
  const [expanded,       setExpanded]       = useState(false);
  const [adjustMode,     setAdjustMode]     = useState(false);
  const [editingSlotIds, setEditingSlotIds] = useState<Set<string>>(new Set());
  const meta = STRATEGY_META[scenario.strategy];

  const resourceGroups = useMemo(
    () => groupSlotsByResource(scenario.slots, slotEdits, resources),
    [scenario.slots, slotEdits, resources],
  );

  const utilisationDays = resourceDays ? resourceDays.daysFor(scenario) : scenario.resourceDailyUtilisation;
  const scoringUtilisationDays = resourceDays ? resourceDays.scoringDaysFor(scenario) : scenario.resourceDailyUtilisation;
  const scenarioResourceDays = useMemo<ScenarioResourceDays | undefined>(
    () => (resourceDays && scenario.planAvailabilityContext
      ? {
          needs: resourceId => resourceDays.needsDays(scenario, resourceId),
          load:  resourceId => resourceDays.loadDays(scenario, resourceId),
        }
      : undefined),
    [resourceDays, scenario],
  );



  function openAdjustMode() {
    setExpanded(true);
    setAdjustMode(true);
    setEditingSlotIds(new Set(scenario.slots.map(s => s.id)));
  }

  function closeAdjustMode() {
    setAdjustMode(false);
    setEditingSlotIds(new Set());
  }

  function toggleSlotEdit(slotId: string) {
    setEditingSlotIds(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId);
      else next.add(slotId);
      return next;
    });
  }

  const proposedBookingCount = useMemo(
    () => countProposedBookings(scenario.slots),
    [scenario.slots],
  );

  const editCount = useMemo(
    () => scenario.slots.filter(s => slotEdits.has(s.id) && Object.keys(slotEdits.get(s.id)!).length > 0).length,
    [scenario.slots, slotEdits],
  );

  // True when the planner fell back to the full pool because no role-matched
  // resource was available (PRD §7.2 role near-hard filter fallback).
  const hasRoleFallback = useMemo(
    () => scenario.slots.some(s => s.roleFallback === true),
    [scenario.slots],
  );

  function handleCardClick() {
    if (!expanded) onSelect();
    else onSelect();
  }


  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={handleCardClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleCardClick()}
      className={cn(
        'rounded-xl border bg-card transition-all duration-200 cursor-pointer outline-none select-none',
        meta.accent,
        isSelected
          ? `ring-2 ${meta.ringAccent} border-[var(--accent-border)] shadow-md`
          : 'hover:shadow-sm hover:border-[var(--accent-border)]/60',
        isAccepted && 'pointer-events-none',
      )}
    >
      {/* ── Top section ── */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border"
            style={{
              backgroundColor: 'var(--accent-bg-outline)',
              borderColor:     'var(--accent-border)',
              color:           'var(--accent-text-on-outline)',
            }}
          >
            {meta.icon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold">{scenario.label}</h3>
              {meta.recommended && !isAccepted && (
                <Badge variant="accent" accent="teal" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <Star className="h-2.5 w-2.5 fill-current" />
                  Recommended
                </Badge>
              )}
              {isSelected && !isAccepted && (
                <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <Check className="h-2.5 w-2.5" />
                  Selected
                </Badge>
              )}
              {isAccepted && (
                <Badge variant="accent" accent="teal" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <CheckCircle2 className="h-2.5 w-2.5" />
                  Accepted
                </Badge>
              )}
              {editCount > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                  <Pencil className="h-2.5 w-2.5" />
                  {editCount} edit{editCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{scenario.description}</p>
          </div>

          <div className="shrink-0 text-right flex gap-4">
            <div>
              <div className="text-lg font-bold tabular-nums leading-none">{scenario.totalHours}h</div>
              <div className="text-[10px] text-muted-foreground">covered</div>
            </div>
            <div>
              <div className="text-lg font-bold tabular-nums leading-none">{scenario.resourceCount}</div>
              <div className="text-[10px] text-muted-foreground">{scenario.resourceCount === 1 ? 'person' : 'people'}</div>
            </div>
          </div>
        </div>

        {/* Coverage bar */}
        <div className="mt-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              Capacity coverage
            </span>
            <span className="text-xs font-bold tabular-nums" style={{ color: 'var(--accent-text-on-light)' }}>
              {scenario.coveragePercent}%
            </span>
          </div>
          <Progress value={scenario.coveragePercent} className="h-1.5" />
        </div>

        {/* Role fallback warning — no role-matched resource was available */}
        {hasRoleFallback && (
          <Alert variant="warning" size="compact" className="mt-3">
            <AlertDescription className="text-xs">
              {requestRole && requestRole.trim() !== ''
                ? `No resources match the requested role “${requestRole}” — showing all available resources instead.`
                : 'No role-matched resources available — showing all available resources.'}
            </AlertDescription>
          </Alert>
        )}

        {/* Date range */}
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          <span>{fmtDate(scenario.earliestStart)}</span>
          <ArrowRight className="h-3 w-3 shrink-0" />
          <span>{fmtDate(scenario.latestEnd)}</span>
        </div>

        {/* Action buttons — always visible when card is selected */}
        {isSelected && !isAccepted && (
          <div className="mt-3 flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {onPreviewOnCalendar && (
              <PreviewButton scenario={scenario} onPreview={onPreviewOnCalendar} />
            )}
            {adjustMode ? (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-2 gap-1 border-primary/40 text-primary hover:bg-primary/5"
                onClick={closeAdjustMode}
              >
                <Check className="h-3 w-3" />
                Done adjusting
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-2 gap-1"
                onClick={openAdjustMode}
              >
                <Pencil className="h-3 w-3" />
                Adjust bookings
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ── Pros / Cons ── */}
      <div className="px-4 pb-4 grid grid-cols-2 gap-4 border-t pt-3">
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <ThumbsUp className="h-3 w-3" style={{ color: 'var(--accent-text-on-outline)' }} />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Pros</span>
          </div>
          <ul className="space-y-1.5">
            {scenario.pros.map((p, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: 'var(--accent-text-on-outline)' }} />
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <ThumbsDown className="h-3 w-3 text-muted-foreground/50" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Cons</span>
          </div>
          <ul className="space-y-1.5">
            {scenario.cons.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground/30 shrink-0" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Proposed bookings toggle ── */}
      <div className="border-t" onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent/20 transition-colors"
          onClick={() => {
            if (!isSelected && !isAccepted) onSelect();
            setExpanded(x => !x);
          }}
          aria-expanded={expanded}
        >
          <span className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {proposedBookingCount} proposed booking{proposedBookingCount !== 1 ? 's' : ''}
            {editCount > 0 && (
              <span className="text-primary font-semibold">· {editCount} edited</span>
            )}
          </span>
          <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', expanded && 'rotate-180')} />
        </button>

        {expanded && (
          <div className="px-3 pb-3 space-y-3">

            {/* Adjust mode active banner */}
            {isSelected && !isAccepted && adjustMode && (
              <div className="flex items-center justify-between rounded-lg px-3 py-2 border bg-primary/5 border-primary/20">
                <div className="flex items-center gap-2">
                  <Pencil className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="text-xs font-medium text-primary">
                    Editing all bookings — adjust dates, hours or resource
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {editCount > 0 && (
                    <button
                      type="button"
                      className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                      onClick={() => {
                        scenario.slots.forEach(s => onSlotReset(s.id));
                      }}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset all
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-[10px] text-primary font-medium hover:text-primary/80 flex items-center gap-1 transition-colors"
                    onClick={closeAdjustMode}
                  >
                    <Check className="h-3 w-3" />
                    Done
                  </button>
                </div>
              </div>
            )}

            {/* Edits summary banner — only when adjustments have been made (not in adjust mode) */}
            {isSelected && !isAccepted && !adjustMode && editCount > 0 && (
              <div className="flex items-center justify-between rounded-lg px-3 py-2 border bg-primary/5 border-primary/20">
                <div className="flex items-center gap-2">
                  <Pencil className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="text-xs font-medium text-primary">
                    {editCount} booking{editCount !== 1 ? 's' : ''} adjusted
                  </span>
                </div>
                <button
                  type="button"
                  className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  onClick={() => {
                    scenario.slots.forEach(s => onSlotReset(s.id));
                    setEditingSlotIds(new Set());
                  }}
                >
                  <RotateCcw className="h-3 w-3" />
                  Reset all
                </button>
              </div>
            )}

            {/* Resource-grouped accordion cards */}
            {resourceGroups.map(group => (
              <ResourceSlotGroupCard
                key={group.resourceId}
                group={group}
                slotEdits={slotEdits}
                editingSlotIds={editingSlotIds}
                isEditable={isSelected && !isAccepted}
                scenarioTotalHours={scenario.totalHours}
                resources={resources}
                existingBookings={existingBookings}
                requestSkills={requestSkills}
                requestRole={requestRole}
                utilisationByResourceId={scenario.resourceUtilisation}
                utilisationDaysByResourceId={utilisationDays}
                scoringUtilisationDaysByResourceId={scoringUtilisationDays}
                availabilityByResourceId={scenario.resourceAvailability}
                resourceDays={scenarioResourceDays}
                slotErrors={slotErrors}
                onSelectScenario={!isSelected && !isAccepted ? onSelect : undefined}
                onSlotEdit={onSlotEdit}
                onSlotReset={onSlotReset}
                onSlotValidityChange={onSlotValidityChange}
                onToggleSlotEdit={toggleSlotEdit}
              />
            ))}

            {/* Hint when scenario is not selected */}
            {!isSelected && (
              <p className="text-center text-[10px] text-muted-foreground py-1">
                Select this scenario to adjust bookings
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function GeneratingSkeleton() {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-5 text-center">
      <div className="relative">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 border border-primary/20">

        </div>
        <div className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full bg-primary">
          <Loader2 className="h-3 w-3 text-primary-foreground animate-spin" />
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold">Generating planning scenarios…</p>
        <p className="text-xs text-muted-foreground mt-1.5 max-w-xs leading-relaxed">
          Analysing resource availability, role match and capacity across your team
        </p>
      </div>
      <div className="w-full space-y-3 mt-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-xl border bg-muted/30 p-4 animate-pulse" style={{ animationDelay: `${i * 150}ms` }}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-muted/60 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 w-32 rounded bg-muted/60" />
                <div className="h-3 w-48 rounded bg-muted/40" />
              </div>
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-muted/60" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Budget health colour helpers (EDS semantic) ─────────────────────────────

const HEALTH_COLORS: Record<'teal' | 'warning' | 'destructive', {
  progress: string;
  text: string;
  bg: string;
}> = {
  teal:        { progress: 'bg-[var(--accent-teal-500)]',        text: 'text-[var(--accent-teal-700)]',        bg: 'bg-[var(--accent-teal-50)]' },
  warning:     { progress: 'bg-[var(--accent-warning-500)]',     text: 'text-[var(--accent-warning-700)]',     bg: 'bg-[var(--accent-warning-50)]' },
  destructive: { progress: 'bg-destructive',                     text: 'text-destructive',                     bg: 'bg-destructive/5' },
};

/** Trim floating point noise so 18.500000001h renders as 18.5h. */
function fmtBudgetHours(hours: number): number {
  return Math.round(hours * 10) / 10;
}

// ── Project Budget Context panel (for left panel) ─────────────────────────────

interface ProjectBudgetPanelProps {
  request: ResourceRequest;
  budgetData: EBProjectsBudgetComposite | null;
  budgetLoading: boolean;
  budgetError: string | null;
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'];
  projectMonthWindow: ProjectMonthWindow;
}

function ProjectBudgetPanel({
  request,
  budgetData,
  budgetLoading,
  budgetError,
  roleBudgets,
  projectMonthWindow,
}: ProjectBudgetPanelProps) {
  if (budgetLoading) {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Project Resource Budget</div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-2 w-full" />
      </div>
    );
  }

  if (budgetError || !budgetData) {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Project Resource Budget</div>
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5 border border-dashed border-border/60">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="leading-relaxed">No resource budget configured for this project</span>
        </div>
      </div>
    );
  }

  const matchedRole = findRoleBudget(
    roleBudgets, request.role, request.roleCode, request.costCentreCode,
  );

  if (!matchedRole) {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Project Resource Budget</div>
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5 border border-dashed border-border/60">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="leading-relaxed">No budget for {request.role} role in this project</span>
        </div>
      </div>
    );
  }

  // The PSA role budget only carries timesheet actuals, so scheduler bookings
  // made against this request are added on top — the same booked hours the
  // request row's Budget Context reports.
  const bookedHours   = request.allocatedHours ?? 0;
  const manualHours   = request.manualBookedHours ?? 0;
  const psaActualHours = matchedRole.actualHours ?? 0;
  const consumedHours = psaActualHours + bookedHours;
  const remainingHours = Math.max(0, matchedRole.budgetHours - consumedHours);
  const percentUsed = matchedRole.budgetHours > 0
    ? (consumedHours / matchedRole.budgetHours) * 100
    : 0;

  const health = budgetHealthAccent(percentUsed);
  const colors = HEALTH_COLORS[health];
  const monthlyBudget = parseMonthlyHours(matchedRole.monthlyHours);
  const demandHints = request.monthlyHourHints ?? {};
  const monthlyBooked = request.monthlyAllocatedHours ?? {};

  // Build merged month keys from both budget and demand hints, less the
  // budget-only months outside the project's LIVE window.
  //
  // The window is the project's current one, not the one the demand froze when
  // it was raised: extending the project shows the months it now runs for, and
  // shortening it drops the months the budget kept. A month the request asks for
  // or already has bookings in stays either way (see liveBreakdownMonths).
  const allMonths = liveBreakdownMonths({
    monthlyBudget,
    demandHints,
    monthlyBooked,
    projectWindow: projectMonthWindow,
  });

  return (
    <div className="space-y-3">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
        Project Resource Budget
      </div>

      {/* Role summary */}
      <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium truncate">{matchedRole.roleName}</span>
          <Badge variant="accent" accent={health} className="text-[10px] px-1.5 py-0 h-4 shrink-0">
            {Math.round(percentUsed)}% used
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[10px] text-muted-foreground">Budget</div>
            <div className="text-xs font-bold tabular-nums">{fmtBudgetHours(matchedRole.budgetHours)}h</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground">Consumed</div>
            <div className="text-xs font-bold tabular-nums">{fmtBudgetHours(consumedHours)}h</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground">Remaining</div>
            <div className={cn('text-xs font-bold tabular-nums', colors.text)}>{fmtBudgetHours(remainingHours)}h</div>
          </div>
        </div>
        {bookedHours > 0 && (
          <div className="text-[10px] text-muted-foreground tabular-nums">
            Includes {fmtBudgetHours(bookedHours)}h booked for this role
            {manualHours > 0 && ` (${fmtBudgetHours(manualHours)}h booked outside this request)`}
            {psaActualHours > 0 && ` and ${fmtBudgetHours(psaActualHours)}h actuals`}
          </div>
        )}
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all', colors.progress)}
            style={{ width: `${Math.min(100, percentUsed)}%` }}
          />
        </div>
      </div>

      {/* Monthly breakdown comparison */}
      {allMonths.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
            Monthly Breakdown
          </div>
          <div className="rounded-lg border border-border/50 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-5 gap-0 px-2 py-1.5 bg-muted/40 border-b border-border/40">
              <span className="text-[9px] font-semibold text-muted-foreground uppercase">Month</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Budg.</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Used</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Avail.</span>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase text-right">Req.</span>
            </div>
            {/* Rows */}
            {allMonths.map((month) => {
              const budgeted = monthlyBudget[month] ?? 0;
              // Timesheet actuals have no per-month breakdown, so they are
              // pro-rated across the budget; bookings are known per month.
              const ratio = matchedRole.budgetHours > 0
                ? psaActualHours / matchedRole.budgetHours
                : 0;
              const consumed = Math.round(budgeted * ratio) + (monthlyBooked[month] ?? 0);
              const available = Math.max(0, budgeted - consumed);
              const requested = demandHints[month] ?? 0;
              const overBudget = requested > 0 && requested > available;

              const [yr, mo] = month.split('-').map(Number);
              const label = new Date(yr, mo - 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

              return (
                <div
                  key={month}
                  className={cn(
                    'grid grid-cols-5 gap-0 px-2 py-1 border-b border-border/20 last:border-b-0',
                    overBudget && 'bg-destructive/5',
                  )}
                >
                  <span className="text-[10px] text-muted-foreground">{label}</span>
                  <span className="text-[10px] tabular-nums text-right">{fmtBudgetHours(budgeted)}h</span>
                  <span className="text-[10px] tabular-nums text-right text-muted-foreground">{fmtBudgetHours(consumed)}h</span>
                  <span className="text-[10px] tabular-nums text-right">{fmtBudgetHours(available)}h</span>
                  <span className={cn(
                    'text-[10px] tabular-nums text-right font-medium',
                    overBudget ? 'text-destructive' : 'text-foreground',
                  )}>
                    {requested > 0 ? `${fmtBudgetHours(requested)}h` : '-'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Left panel — request summary ──────────────────────────────────────────────

interface RequestSummaryPanelProps {
  request: ResourceRequest;
  budgetData: EBProjectsBudgetComposite | null;
  budgetLoading: boolean;
  budgetError: string | null;
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'];
  projectMonthWindow: ProjectMonthWindow;
}

function RequestSummaryPanel({ request, budgetData, budgetLoading, budgetError, roleBudgets, projectMonthWindow }: RequestSummaryPanelProps) {
  const priorityAccent = request.priority === 'critical' ? 'destructive'
    : request.priority === 'high'   ? 'warning'
    : request.priority === 'medium' ? 'info'
    : 'teal';

  // The live budgeted per-month picture: the role's current month hours over the
  // months the project currently runs for. The demand froze its own spread when
  // it was raised, so amending the project in EBProjects — extending it and
  // budgeting the new months, or trimming months away — has to read here without
  // the demand being declined and re-raised.
  //
  // Fully allocated requests have nothing left to plan, so their per-month
  // booked hours are shown instead — those are real bookings, not a budget.
  // Without a live budget to read (it failed to load, or the role has none) the
  // demand's own planner spread is still shown rather than nothing.
  const allocatedHours   = request.allocatedHours ?? 0;
  const manualBookedHours = request.manualBookedHours ?? 0;
  const originalHours    = request.originalHoursRequested ?? request.totalHoursRequested;
  const fullyAllocated   = request.totalHoursRequested <= 0 && allocatedHours > 0;
  const monthlyAllocated = request.monthlyAllocatedHours ?? {};
  const matchedRole      = findRoleBudget(
    roleBudgets, request.role, request.roleCode, request.costCentreCode,
  );
  const liveSpread       = matchedRole
    ? liveMonthlySpread(parseMonthlyHours(matchedRole.monthlyHours), projectMonthWindow)
    : {};
  const monthlySpread    = fullyAllocated
    ? monthlyAllocated
    : Object.keys(liveSpread).length > 0
      ? liveSpread
      : computeRequestMonthlyHints(request);
  const spreadLabel      = fullyAllocated ? 'Monthly Allocation (booked)' : 'Monthly Spread (hint)';

  // The start the project currently runs from, driving the monthly sections.
  const liveStart        = liveStartMonth(projectMonthWindow, liveSpread, request.monthlyHourHints ?? {});

  const startDateLabel   = fmtDate(summaryStartDate(request.requestedStartDate, liveStart));

  return (
    <div className="flex flex-col gap-5 h-full">
      <div>
        <div className="flex items-start justify-between gap-2 mb-1">
          <h2 className="text-base font-bold leading-tight">{request.projectName}</h2>
          <Badge variant="accent" accent={priorityAccent} className="shrink-0 capitalize text-xs">
            {request.priority}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {request.projectCode && (
            <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {request.projectCode}
            </span>
          )}
          {request.costCentreCode && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              <Building2 className="h-2.5 w-2.5 shrink-0" />
              <span className="font-mono">{request.costCentreCode}</span>
              {request.costCentreName && <span>{request.costCentreName}</span>}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span>
          Requested by{' '}
          <span className="font-medium text-foreground">{request.requestedByName}</span>
        </span>
      </div>

      <Separator />

      <div className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {fullyAllocated ? 'Hours Booked' : allocatedHours > 0 ? 'Hours Remaining' : 'Total Hours'}
            </div>
            <div className="text-xl font-bold tabular-nums leading-tight">
              {fullyAllocated ? allocatedHours : request.totalHoursRequested}h
            </div>
            {allocatedHours > 0 && (
              <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                {allocatedHours}h of {originalHours}h already booked
                {manualBookedHours > 0 && `, incl. ${manualBookedHours}h booked outside this request`}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
          <User className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Role Needed</div>
            <div className="text-sm font-semibold leading-tight">{request.role}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
          <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Start Date</div>
            <div className="text-sm font-semibold leading-tight">{startDateLabel}</div>
          </div>
        </div>
      </div>

      {request.skills.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Tag className="h-3 w-3 text-muted-foreground" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              {skillsSectionLabel(request.skillsDetailed)}
            </span>
          </div>
          <RequestedSkillBadges skills={request.skills} skillsDetailed={request.skillsDetailed} />
        </div>
      )}

      {Object.keys(monthlySpread).length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {spreadLabel}
          </div>
          <div className="space-y-1.5">
            {Object.entries(monthlySpread).sort(([a], [b]) => a.localeCompare(b)).map(([month, hours]) => (
              <div key={month} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{month}</span>
                <span className="font-medium tabular-nums">{hours}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Project Resource Budget ── */}
      <ProjectBudgetPanel
        request={request}
        budgetData={budgetData}
        budgetLoading={budgetLoading}
        budgetError={budgetError}
        roleBudgets={roleBudgets}
        projectMonthWindow={projectMonthWindow}
      />

      <Separator />

      {request.notes && (
        <div className="flex-1">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Notes</div>
          <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-3 italic">
            "{request.notes}"
          </p>
        </div>
      )}
    </div>
  );
}

// ── Accepted allocation summary ───────────────────────────────────────────────

interface ResourceAllocation {
  resourceId:   string;
  resourceName: string;
  resourceRole?: string;
  photoUrl?:    string;
  hours:        number;
  bookings:     AcceptedRequestBooking[];
  firstStart:   string;
  lastEnd:      string;
  months:       string[];
}

/** One booking group per resource, ordered by hours booked (largest first). */
function groupByResource(bookings: AcceptedRequestBooking[]): ResourceAllocation[] {
  const groups = new Map<string, ResourceAllocation>();
  for (const booking of bookings) {
    const existing = groups.get(booking.resourceId);
    if (existing) {
      existing.hours += booking.hours;
      existing.bookings.push(booking);
      if (booking.startTime < existing.firstStart) existing.firstStart = booking.startTime;
      if (booking.endTime   > existing.lastEnd)    existing.lastEnd   = booking.endTime;
      if (!existing.months.includes(booking.monthKey)) existing.months.push(booking.monthKey);
    } else {
      groups.set(booking.resourceId, {
        resourceId:   booking.resourceId,
        resourceName: booking.resourceName,
        resourceRole: booking.resourceRole,
        photoUrl:     booking.resourcePhotoUrl,
        hours:        booking.hours,
        bookings:     [booking],
        firstStart:   booking.startTime,
        lastEnd:      booking.endTime,
        months:       [booking.monthKey],
      });
    }
  }
  return [...groups.values()]
    .map(group => ({ ...group, months: [...group.months].sort() }))
    .sort((a, b) => b.hours - a.hours);
}

function monthLabel(monthKey: string) {
  try { return format(parseISO(`${monthKey}-01`), 'MMM yyyy'); } catch { return monthKey; }
}

/** Collapsed summary for one resource; expands to the individual bookings. */
function ResourceAllocationCard({
  allocation,
  totalHours,
}: {
  allocation: ResourceAllocation;
  totalHours: number;
}) {
  const [open, setOpen] = useState(false);
  const share = totalHours > 0 ? Math.round((allocation.hours / totalHours) * 100) : 0;

  const byMonth = new Map<string, AcceptedRequestBooking[]>();
  for (const booking of allocation.bookings) {
    byMonth.set(booking.monthKey, [...(byMonth.get(booking.monthKey) ?? []), booking]);
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn(
        'rounded-lg border overflow-hidden transition-colors',
        open ? 'border-border' : 'border-border/60',
      )}>
        <CollapsibleTrigger className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8 shrink-0">
              {allocation.photoUrl && <AvatarImage src={allocation.photoUrl} alt={allocation.resourceName} />}
              <AvatarFallback className="text-[10px]">{initials(allocation.resourceName)}</AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold truncate">{allocation.resourceName}</span>
                {allocation.resourceRole && (
                  <Badge variant="outline" className="text-[10px] shrink-0">{allocation.resourceRole}</Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                {allocation.bookings.length} booking{allocation.bookings.length !== 1 ? 's' : ''}
                {' · '}{fmtDate(allocation.firstStart)} – {fmtDate(allocation.lastEnd)}
                {' · '}{allocation.months.length} month{allocation.months.length !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="shrink-0 text-right">
              <div className="text-sm font-bold tabular-nums">{allocation.hours}h</div>
              <div className="text-[10px] text-muted-foreground tabular-nums">{share}% of booked</div>
            </div>

            <ChevronDown className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )} />
          </div>

          <Progress value={share} className="h-1 mt-2.5" />

          {/* Monthly totals at a glance; the expanded view lists them per booking */}
          {!open && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {allocation.months.map(month => (
                <span
                  key={month}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/20 px-2 py-0.5 text-[10px]"
                >
                  <span className="text-muted-foreground">{monthLabel(month)}</span>
                  <span className="font-semibold tabular-nums">
                    {sumHours((byMonth.get(month) ?? []).map(b => b.hours))}h
                  </span>
                </span>
              ))}
            </div>
          )}
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-border/60">
            {allocation.months.map(month => (
              <div key={month}>
                <div className="flex items-center justify-between px-4 py-1.5 bg-muted/30">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {monthLabel(month)}
                  </span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {sumHours((byMonth.get(month) ?? []).map(b => b.hours))}h
                  </span>
                </div>
                <div className="divide-y divide-border/40">
                  {(byMonth.get(month) ?? []).map(booking => (
                    <div key={booking.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-4 py-2 text-xs">
                      <span className="text-muted-foreground truncate">{fmtDate(booking.startTime)}</span>
                      <span className="text-muted-foreground tabular-nums">
                        {formatTime(booking.startTime)}–{formatTime(booking.endTime)}
                      </span>
                      <Badge variant="secondary" className="text-[10px] capitalize">{booking.status}</Badge>
                      <span className="font-medium tabular-nums w-12 text-right">{booking.hours}h</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/**
 * Read-only record of an accepted plan: an overview plus one collapsed card per
 * resource, each expanding to the bookings it holds.
 *
 * The accepted scenario is never persisted, so the bookings it created (fetched
 * by demand id) are the record of what exists. When they cannot be loaded — demo
 * mode, or a request whose bookings predate demand stamping — the demand's
 * per-month allocated hours are shown instead.
 */
function AcceptedAllocationSummary({
  request,
  bookings,
  loading,
}: {
  request: ResourceRequest;
  bookings: AcceptedRequestBooking[];
  loading: boolean;
}) {
  const monthly   = request.monthlyAllocatedHours ?? {};
  const booked    = request.allocatedHours ?? 0;
  const remaining = request.totalHoursRequested;

  const allocations = useMemo(() => groupByResource(bookings), [bookings]);
  const bookedFromBookings = sumHours(allocations.map(a => a.hours));
  const monthsCovered = new Set(bookings.map(b => b.monthKey)).size;

  return (
    <div className="space-y-4">
      <Alert variant={remaining > 0 ? 'info' : 'success'} size="compact">
        <AlertTitle>{remaining > 0 ? 'Allocation in progress' : 'Allocation complete'}</AlertTitle>
        <AlertDescription>
          {`${booked}h of the ${request.originalHoursRequested ?? booked + remaining}h requested `}
          {bookings.length > 0
            ? `has been allocated across ${bookings.length} booking${bookings.length !== 1 ? 's' : ''}.`
            : 'has been allocated.'}
          {remaining > 0
            ? ` ${remaining}h remains outstanding.`
            : ' No outstanding hours remain.'}
        </AlertDescription>
      </Alert>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : bookings.length > 0 ? (
        <>
          {/* Overview — the summary the drawer opens on */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Booked',    value: `${bookedFromBookings}h` },
              { label: 'Resources', value: String(allocations.length) },
              { label: 'Bookings',  value: String(bookings.length) },
              { label: 'Months',    value: String(monthsCovered) },
            ].map(stat => (
              <div key={stat.label} className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{stat.label}</div>
                <div className="text-sm font-bold tabular-nums mt-0.5">{stat.value}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Allocation by resource
            </span>
            <span className="text-[10px] text-muted-foreground">Select a resource to view its bookings</span>
          </div>

          <div className="space-y-2">
            {allocations.map(allocation => (
              <ResourceAllocationCard
                key={allocation.resourceId}
                allocation={allocation}
                totalHours={bookedFromBookings}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-b border-border/60">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Booked hours by month
            </span>
            <Badge variant="secondary" className="text-[10px] tabular-nums">{booked}h total</Badge>
          </div>
          {Object.keys(monthly).length > 0 ? (
            <div className="divide-y divide-border/40">
              {Object.keys(monthly).sort().map(month => (
                <div key={month} className="flex items-center justify-between px-4 py-2 text-xs">
                  <span className="text-muted-foreground">{month}</span>
                  <span className="font-medium tabular-nums">{monthly[month]}h</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-3 text-xs text-muted-foreground">
              No booking detail available for this request.
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground leading-relaxed">
        These bookings are on the calendar for {request.projectName} and can be edited there.
      </p>
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export interface ResourceRequestPlanDrawerProps {
  open:                  boolean;
  onOpenChange:          (open: boolean) => void;
  request:               ResourceRequest | null;
  resources:             Resource[];
  existingBookings:      Booking[];
  scenarios:             PlanScenario[];
  isGenerating:          boolean;
  /**
   * The plan window's bookings and absences, when the caller already read them
   * to generate the plan. Supplying them stops the drawer asking for the same
   * window again; it fetches them itself when they are absent.
   */
  planWindowBookings?:   Booking[];
  planWindowAbsences?:   Absence[];
  /**
   * Whether the caller is reading that window right now. Its bookings and
   * absences arrive when the read finishes, so the drawer waits for them rather
   * than asking for the same window alongside it.
   */
  planWindowPending?:    boolean;
  /** Bookings a previously accepted plan created for this request */
  acceptedBookings?:     AcceptedRequestBooking[];
  acceptedBookingsLoading?: boolean;
  /** 'bookings' shows only what has already been booked, without planning the remainder */
  viewMode?:             'plan' | 'bookings';
  /** Switches a 'bookings' view over to planning the outstanding hours */
  onPlanRemaining?:      () => void;
  /**
   * Accepts the selected scenario. `slots` carry any in-drawer adjustments, so the
   * bookings created are exactly the ones the user reviewed.
   */
  onAccept:              (scenarioId: string, slots: ProposedBookingSlot[], editCount: number, bookingTypeId?: number, unavailableDaysByResourceId?: Record<string, UnavailableDay[]>) => Promise<void>;
  /** Called when user clicks "Preview on Calendar" for a scenario */
  onPreviewOnCalendar?:  (preview: PlanPreviewScenario) => void;
}

/**
 * The scenario selection and slot edits made in the drawer, scoped to the plan they
 * were made on. Scenario and slot ids repeat from one request's plan to the next, so
 * another request or a regenerated plan starts again from the default scenario with
 * no edits and no errors. `planVersion` changes with each plan, so per-card state
 * keyed on it starts afresh too.
 */
export function usePlanEditState(requestId: string | null, scenarios: PlanScenario[]) {
  const plan = scenarios.length > 0 ? scenarios : null;
  const [editedPlan, setEditedPlan] = useState({ requestId, plan, version: 0 });
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  // Per-slot edits: keyed by slot ID, isolated per scenario selection
  const [slotEdits, setSlotEdits] = useState<SlotEdits>(new Map());
  // Validation messages for slots being edited; a non-empty map blocks Accept
  const [slotErrors, setSlotErrors] = useState<Map<string, string>>(new Map());

  if (editedPlan.requestId !== requestId || editedPlan.plan !== plan) {
    setEditedPlan({ requestId, plan, version: editedPlan.version + 1 });
    setSelectedScenarioId(null);
    setSlotEdits(new Map());
    setSlotErrors(new Map());
  }

  return {
    planVersion: editedPlan.version,
    selectedScenarioId, setSelectedScenarioId,
    slotEdits, setSlotEdits,
    slotErrors, setSlotErrors,
  };
}

export function ResourceRequestPlanDrawer({
  open,
  onOpenChange,
  request,
  resources,
  existingBookings: passedBookings,
  scenarios,
  isGenerating,
  planWindowBookings: passedWindowBookings,
  planWindowAbsences: passedWindowAbsences,
  planWindowPending = false,
  acceptedBookings = [],
  acceptedBookingsLoading = false,
  viewMode = 'plan',
  onPlanRemaining,
  onAccept,
  onPreviewOnCalendar,
}: ResourceRequestPlanDrawerProps) {
  const [isAccepting, setIsAccepting]               = useState(false);
  const [confirmOpen, setConfirmOpen]               = useState(false);
  const {
    planVersion,
    selectedScenarioId, setSelectedScenarioId,
    slotEdits, setSlotEdits,
    slotErrors, setSlotErrors,
  } = usePlanEditState(request?.id ?? null, scenarios);
  const planResourceDays = usePlanResourceDays(scenarios, planVersion);


  const defaultScenario = useMemo(() => {
    if (!scenarios.length) return null;
    return scenarios.find(s => s.strategy === 'spread-load') ?? scenarios[0];
  }, [scenarios]);

  const effectiveSelected = selectedScenarioId ?? defaultScenario?.id ?? null;
  const selectedScenario  = scenarios.find(s => s.id === effectiveSelected);
  const selectedBookingCount = countProposedBookings(selectedScenario?.slots ?? []);
  const selectedScenarioDays = planResourceDays.daysFor(selectedScenario);

  // Reset edits when a different scenario is selected
  function handleSelectScenario(id: string) {
    if (id !== effectiveSelected) {
      setSlotEdits(new Map());
      setSlotErrors(new Map());
    }
    setSelectedScenarioId(id);
  }

  const handleSlotEdit = useCallback((slotId: string, patch: SlotEdit) => {
    setSlotEdits(prev => {
      const next = new Map(prev);
      next.set(slotId, { ...(next.get(slotId) ?? {}), ...patch });
      return next;
    });
  }, []);

  const handleSlotReset = useCallback((slotId: string) => {
    setSlotEdits(prev => {
      const next = new Map(prev);
      next.delete(slotId);
      return next;
    });
    setSlotErrors(prev => {
      if (!prev.has(slotId)) return prev;
      const next = new Map(prev);
      next.delete(slotId);
      return next;
    });
  }, []);

  const handleSlotValidityChange = useCallback((slotId: string, message: string | null) => {
    setSlotErrors(prev => {
      if (message === null) {
        if (!prev.has(slotId)) return prev;
        const next = new Map(prev);
        next.delete(slotId);
        return next;
      }
      if (prev.get(slotId) === message) return prev;
      const next = new Map(prev);
      next.set(slotId, message);
      return next;
    });
  }, []);

  const totalEditCount = useMemo(
    () => Array.from(slotEdits.values()).filter(e => Object.keys(e).length > 0).length,
    [slotEdits],
  );

  /**
   * Days a slot may be moved to: never before the request starts, never past the
   * last month the plan allocates to.
   */
  const planDateBounds = useMemo<PlanDateBounds | null>(() => {
    if (!request) return null;
    const monthKeys = [
      ...(selectedScenario?.slots ?? []).map(slot => slot.monthKey),
      ...Object.keys(request.monthlyAllocatedHours ?? {}),
    ].filter(Boolean);
    return computePlanDateBounds(request.requestedStartDate, monthKeys, request.requestedEndDate);
  }, [request, selectedScenario]);

  /**
   * The bookings the drawer judges availability against. In live mode the caller
   * has none, so the plan window's own bookings are fetched: without their time
   * intervals a moved day could only be checked on hours, and would land on top
   * of an existing booking.
   */
  const fetchedWindowBookings = usePlanWindowBookings(
    planDateBounds,
    shouldReadPlanWindow(open, passedWindowBookings, planWindowPending),
  );
  const planWindowBookings = passedWindowBookings ?? fetchedWindowBookings;
  const existingBookings = useMemo(() => {
    if (planWindowBookings.length === 0) return passedBookings;
    const known = new Set(passedBookings.map(b => String(b.id)));
    return [...passedBookings, ...planWindowBookings.filter(b => !known.has(String(b.id)))];
  }, [passedBookings, planWindowBookings]);

  const { settings } = useScheduler();
  const fetchedAbsences = useRequestAbsences(
    request,
    shouldReadPlanWindow(open, passedWindowAbsences, planWindowPending),
  );
  const absences = passedWindowAbsences ?? fetchedAbsences;

  /** The window a day's bookings sit in, which also places its half days. */
  const businessHours = useMemo(() => ({
    start: settings.businessHoursStart ?? DEFAULT_BUSINESS_HOURS_START,
    end: settings.businessHoursEnd ?? DEFAULT_BUSINESS_HOURS_END,
  }), [settings.businessHoursStart, settings.businessHoursEnd]);

  /**
   * The bank holidays and absences taking hours off each planned resource's days
   * across the plan's window, so a day nobody can work is judged as such
   * wherever a slot is measured or validated.
   */
  const planUnavailableDaysByResourceId = useMemo(() => {
    const slots = selectedScenario?.slots ?? [];
    if (!planDateBounds || slots.length === 0) return undefined;
    const from = parseISO(planDateBounds.minDate);
    const to = parseISO(planDateBounds.maxDate);
    if (!isValid(from) || !isValid(to)) return undefined;
    const resourceById = new Map(resources.map(r => [String(r.id), r]));
    return unavailableDaysForResources({
      resourceIds: slots.map(slot => String(slot.resourceId)),
      startDate: from,
      endDate: to,
      bankHolidays: mockBankHolidays,
      absences,
      workingProfileFor: resourceId => effectiveWorkingProfile(
        resourceById.get(resourceId), settings.globalWorkingProfile,
      ),
      businessHoursStart: businessHours.start,
      businessHoursEnd: businessHours.end,
    });
  }, [
    selectedScenario, planDateBounds, resources, absences,
    settings.globalWorkingProfile, businessHours,
  ]);

  /** Slots the user has adjusted: their hours are theirs, not the planner's. */
  const editedSlotIds = useMemo(
    () => new Set(
      Array.from(slotEdits)
        .filter(([, edit]) => Object.keys(edit).length > 0)
        .map(([slotId]) => slotId),
    ),
    [slotEdits],
  );

  /**
   * Selected scenario's slots with any in-drawer edits applied.
   *
   * A suggested slot is also reduced to the hours its resource really has free,
   * as a safety net for any residual drift between the hours the planner sized
   * the slot to and the hours the validator measures: a plan nobody has edited
   * is not worth blocking over a tenth of an hour. A slot the user has edited is
   * left exactly as edited, so an edit beyond capacity still blocks.
   */
  const mergedSelectedSlots = useMemo(
    () => clampSuggestedSlotsToCapacity({
      slots: (selectedScenario?.slots ?? []).map(slot => mergeSlot(slot, slotEdits.get(slot.id))),
      editedSlotIds,
      existingBookings,
      resources,
      globalWorkingProfile: settings.globalWorkingProfile,
      reportedBookedDaysByResourceId: selectedScenarioDays,
      unavailableDaysByResourceId: planUnavailableDaysByResourceId,
    }),
    [
      selectedScenario, slotEdits, editedSlotIds, existingBookings, resources,
      settings.globalWorkingProfile, planUnavailableDaysByResourceId, selectedScenarioDays,
    ],
  );

  async function handleConfirmAccept(bookingTypeId?: number) {
    if (!effectiveSelected || invalidSlotCount > 0) return;
    setIsAccepting(true);
    try {
      await onAccept(
        effectiveSelected,
        mergedSelectedSlots,
        totalEditCount,
        bookingTypeId,
        planUnavailableDaysByResourceId,
      );
      setConfirmOpen(false);
      onOpenChange(false);
    } catch {
      // handle silently
    } finally {
      setIsAccepting(false);
    }
  }

  function handlePreviewOnCalendar(scenario: PlanScenario) {
    if (!request || !onPreviewOnCalendar) return;
    onPreviewOnCalendar(buildPlanPreviewScenario(scenario, mergedSelectedSlots, request));
    // Close the drawer so the calendar is fully visible
    onOpenChange(false);
  }

  // ── Budget data for the left panel ─────────────────────────────────────────
  // Read per open, not per mount: the project's dates and role budgets can be
  // amended in EBProjects between two looks at the same request, and the drawer
  // stays mounted the whole time.
  const { budgetData, budgetLoading, budgetError, roleBudgets, projectMonthWindow } =
    useProjectBudget(open ? request?.projectId : null);

  /**
   * The role's budgeted hours per month, so a slot edit that pushes a month's
   * allocation over its budget is reported the same way a capacity breach is.
   * Hours already booked for the request count against the budget too.
   */
  const planBudget = useMemo<PlanBudget | null>(() => {
    if (!request) return null;
    const roleBudget = findRoleBudget(
      roleBudgets, request.role, request.roleCode, request.costCentreCode,
    );
    const budgetedHoursByMonth = parseMonthlyHours(roleBudget?.monthlyHours);
    if (Object.keys(budgetedHoursByMonth).length === 0) return null;

    const bookedHoursByMonth = request.monthlyAllocatedHours ?? {};
    const slotHoursById = new Map(mergedSelectedSlots.map(slot => [
      slot.id,
      monthlyHoursForSlot({
        startDate: slot.startDate,
        endDate: slot.endDate,
        hours: slot.hours,
        dailyAllocations: slot.dailyBreakdown,
      }),
    ]));

    return {
      budgetedHoursByMonth,
      allocatedHoursByMonthExcluding: (slotId: string) => sumMonthlyHours([
        bookedHoursByMonth,
        ...Array.from(slotHoursById)
          .filter(([id]) => id !== slotId)
          .map(([, hours]) => hours),
      ]),
    };
  }, [request, roleBudgets, mergedSelectedSlots]);

  // ── Utilisation capacity inputs, so criteria breakdowns score utilisation the
  // same way the planner and heatmap do ─────────────────────────────────────
  const utilisationContext = useMemo<UtilisationContext>(() => ({
    globalWorkingProfile: settings.globalWorkingProfile,
    bankHolidays: mockBankHolidays,
    absences,
    bookingTypes: settings.bookingTypes,
  }), [settings.globalWorkingProfile, settings.bookingTypes, absences]);

  /**
   * Days the selected scenario books that its resource is already booked on.
   *
   * The planner's own suggestion is checked, not just the user's adjustments: a
   * suggested slot can land on an already-booked day (the backend offers the
   * unbooked part of a partially-booked day), and such a day has to be reported
   * rather than silently allocated. Only `day-booked` is taken from this pass —
   * the capacity judgements behind the other kinds are the planner's own, and
   * the full check still runs when the plan is accepted.
   */
  const suggestedSlotErrors = useMemo(() => {
    const errors = validatePlanSlots({
      slots: mergedSelectedSlots,
      existingBookings,
      resources,
      globalWorkingProfile: settings.globalWorkingProfile,
      reportedBookedDaysByResourceId: selectedScenarioDays,
      unavailableDaysByResourceId: planUnavailableDaysByResourceId,
    });
    const bookedDayErrors = new Map<string, string>();
    for (const [slotId, error] of errors) {
      if (error.kind === 'day-booked' || error.kind === 'day-unavailable') {
        bookedDayErrors.set(slotId, error.message);
      }
    }
    return bookedDayErrors;
  }, [
    mergedSelectedSlots, existingBookings, resources, settings.globalWorkingProfile,
    selectedScenario, planUnavailableDaysByResourceId, selectedScenarioDays,
  ]);

  /** An edit's own error wins: it describes what the user has just changed. */
  const effectiveSlotErrors = useMemo(() => {
    if (suggestedSlotErrors.size === 0) return slotErrors;
    const merged = new Map(suggestedSlotErrors);
    for (const [slotId, message] of slotErrors) merged.set(slotId, message);
    return merged;
  }, [suggestedSlotErrors, slotErrors]);

  /** Slots of the selected scenario that cannot be allocated as they stand. */
  const invalidSlotCount = useMemo(
    () => (selectedScenario?.slots ?? []).filter(slot => effectiveSlotErrors.has(slot.id)).length,
    [selectedScenario, effectiveSlotErrors],
  );

  if (!request) return null;

  const isAccepted = request.status === 'accepted';
  // Nothing outstanding: the planner has no hours to work with, so show what
  // was booked instead of an empty-scenarios warning.
  const fullyAllocated = request.totalHoursRequested <= 0 && (request.allocatedHours ?? 0) > 0;
  // Read-only record of existing bookings, with no scenarios in the way.
  const bookingsOnly = viewMode === 'bookings';

  return (
    <UtilisationContextValue.Provider value={utilisationContext}>
    <BusinessHoursContext.Provider value={businessHours}>
    <PlanDateBoundsContext.Provider value={planDateBounds}>
    <PlanBudgetContext.Provider value={planBudget}>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 overflow-hidden',
          'w-[90vw] max-w-5xl',
          'h-[88vh] max-h-[860px]',
          'flex flex-col',
        )}
      >
        {/* ── Header bar ── */}
        <div className="flex items-center gap-3 px-6 py-4 border-b shrink-0 bg-muted/20">
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-sm font-semibold leading-none">
              Suggested Project Plan
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              {bookingsOnly
                ? 'Bookings already created for this request'
                : fullyAllocated && scenarios.length === 0
                  ? 'This request is fully booked — showing the accepted allocation'
                  : 'Review scenarios, adjust any booking then accept to create them'}
            </DialogDescription>
          </div>
          {!isGenerating && scenarios.length > 0 && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="accent" accent="teal" className="text-xs gap-1 cursor-help shrink-0">
                    <Star className="h-3 w-3 fill-current" />
                    Spread the Load recommended
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-[220px]">
                  Spreading load reduces single points of failure and better utilises team capacity.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* ── Two-panel body ── */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* Left — request summary */}
          <div className="w-72 shrink-0 border-r bg-muted/10 overflow-y-auto px-5 py-5">
            <RequestSummaryPanel
              request={request}
              budgetData={budgetData}
              budgetLoading={budgetLoading}
              budgetError={budgetError}
              roleBudgets={roleBudgets}
              projectMonthWindow={projectMonthWindow}
            />
          </div>

          {/* Right — scenarios */}
          <div className="flex-1 min-w-0 overflow-y-auto px-6 py-5 space-y-4">
            {bookingsOnly ? (
              <>
                <AcceptedAllocationSummary
                  request={request}
                  bookings={acceptedBookings}
                  loading={acceptedBookingsLoading}
                />
                {request.totalHoursRequested > 0 && onPlanRemaining && (
                  <Button size="sm" className="gap-1.5" onClick={onPlanRemaining}>
                    Plan remaining
                  </Button>
                )}
              </>
            ) : isGenerating ? (
              <GeneratingSkeleton />
            ) : scenarios.length === 0 && fullyAllocated ? (
              <AcceptedAllocationSummary
                request={request}
                bookings={acceptedBookings}
                loading={acceptedBookingsLoading}
              />
            ) : scenarios.length === 0 ? (
              <div className="py-8">
                <Alert variant="warning">
                  <AlertTitle>No scenarios could be generated</AlertTitle>
                  <AlertDescription>
                    There may not be enough resource capacity for the requested dates.
                    Try adjusting the start date or reducing the total hours.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <>
                {isAccepted && (
                  <Alert variant="success" size="compact">
                    <AlertTitle>Plan accepted</AlertTitle>
                    <AlertDescription>
                      Bookings have been created from the accepted scenario.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''} generated — select one to review and adjust
                  </span>
                </div>

                {scenarios.map(scenario => (
                  <ScenarioCard
                    key={`${planVersion}:${scenario.id}`}
                    scenario={scenario}
                    isSelected={effectiveSelected === scenario.id}
                    isAccepted={isAccepted && request.acceptedScenarioId === scenario.id}
                    resources={resources}
                    existingBookings={existingBookings}
                    requestSkills={request.skills}
                    requestRole={request.role}
                    slotEdits={slotEdits}
                    slotErrors={effectiveSlotErrors}
                    onSelect={() => handleSelectScenario(scenario.id)}
                    onSlotEdit={handleSlotEdit}
                    onSlotReset={handleSlotReset}
                    onSlotValidityChange={handleSlotValidityChange}
                    onPreviewOnCalendar={onPreviewOnCalendar ? handlePreviewOnCalendar : undefined}
                    resourceDays={planResourceDays}
                  />
                ))}
              </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t bg-card px-6 py-3.5 shrink-0 flex items-center justify-between gap-4">
          <div className="text-xs text-muted-foreground">
            {bookingsOnly ? (
              <span>
                Read-only — these bookings live on the {request.projectName} calendar
              </span>
            ) : isAccepted ? (
              <span className="flex items-center gap-1.5 font-medium" style={{ color: 'var(--accent-teal-600)' }}>
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Plan accepted — bookings created successfully
              </span>
            ) : selectedScenario ? (
              <span className="flex items-center gap-2 flex-wrap">
                <span>
                  <span className="font-medium text-foreground">{selectedScenario.label}</span>
                  {' — '}
                  <span className="font-medium text-foreground">
                    {selectedBookingCount} booking{selectedBookingCount !== 1 ? 's' : ''}
                  </span>
                  {' across '}
                  <span className="font-medium text-foreground">
                    {selectedScenario.resourceCount} resource{selectedScenario.resourceCount !== 1 ? 's' : ''}
                  </span>
                </span>
                {totalEditCount > 0 && (
                  <Badge variant="secondary" className="text-[10px] gap-1 h-5">
                    <Pencil className="h-2.5 w-2.5" />
                    {totalEditCount} booking{totalEditCount !== 1 ? 's' : ''} adjusted
                  </Badge>
                )}
                {invalidSlotCount > 0 && (
                  <span className="text-[10px] font-medium text-destructive">
                    {invalidSlotCount} booking{invalidSlotCount !== 1 ? 's' : ''} need fixing before accepting
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground/60">Select a scenario above to accept</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isAccepting}>
              {bookingsOnly ? 'Close' : 'Cancel'}
            </Button>
            {!isAccepted && !bookingsOnly && (
              <Button
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={!effectiveSelected || isAccepting || invalidSlotCount > 0}
                title={invalidSlotCount > 0
                  ? 'Resolve the highlighted booking errors to continue'
                  : undefined}
                className="gap-1.5"
              >
                {isAccepting ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" />Creating bookings…</>
                ) : totalEditCount > 0 ? (
                  <><Check className="h-3.5 w-3.5" />Accept adjusted plan</>
                ) : (
                  <><Check className="h-3.5 w-3.5" />Accept plan</>
                )}
              </Button>
            )}
          </div>
        </div>

        {selectedScenario && !isAccepted && (
          <AcceptPlanConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            slots={mergedSelectedSlots}
            scenarioLabel={selectedScenario.label}
            projectName={request.projectName}
            role={request.role}
            editCount={totalEditCount}
            isAccepting={isAccepting}
            onConfirm={handleConfirmAccept}
          />
        )}
      </DialogContent>
    </Dialog>
    </PlanBudgetContext.Provider>
    </PlanDateBoundsContext.Provider>
    </BusinessHoursContext.Provider>
    </UtilisationContextValue.Provider>
  );
}
